import React, { useState, useEffect, useCallback, useRef } from 'react';

import AdminService from '../services/adminService';
import { API_ENDPOINTS } from '../utils/config';

import '../css/admin/OrderManagement.css';

import { FaChevronDown, FaChevronUp, FaChevronLeft, FaChevronRight, FaTimes, FaSearch, FaFilter, FaClipboardList, FaStore, FaUser, FaMotorcycle, FaRupeeSign, FaCreditCard, FaTruck, FaCalendar, FaEye, FaEdit, FaInfoCircle, FaBuilding, FaBox, FaExclamationTriangle, FaMapMarkerAlt, FaFileExcel, FaBan, FaUndo, FaCheckCircle, FaTimesCircle } from 'react-icons/fa';

import { MdRefresh } from 'react-icons/md';
import * as XLSX from 'xlsx';

import newOrderSound from '/notification.mp3';



const OrderManagement = () => {

  // Responsive state management
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isTablet, setIsTablet] = useState(window.innerWidth > 768 && window.innerWidth <= 1024);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth > 1024);

  // Update responsive state on window resize
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setWindowWidth(width);
      setIsMobile(width <= 768);
      setIsTablet(width > 768 && width <= 1024);
      setIsDesktop(width > 1024);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Helper function to format status display

  const getStatusDisplay = (order) => {

    if (!order || !order.status) return 'UNKNOWN';

    if ((order.status === 'delivered' || order.status === 'completed') &&

      order.order_type?.toLowerCase() === 'pickup') {

      return 'COMPLETED';

    }

    return order.status.toUpperCase();

  };

  // Helper function to format order ID (e.g. UNREC_53F815 from UNREC_20261003094250_53F815)
  const formatOrderId = (id) => {
    if (!id && id !== 0) return '';
    const str = String(id).trim();
    const cleanStr = str.startsWith('#') ? str.slice(1) : str;
    if (cleanStr.startsWith('UNREC_')) {
      const parts = cleanStr.split('_');
      if (parts.length >= 3) {
        const formatted = `UNREC_${parts[parts.length - 1]}`;
        return str.startsWith('#') ? `#${formatted}` : formatted;
      }
    }
    return str;
  };

  // Helper function to check if order was created manually
  const isManualOrder = (ord = {}) => {
    if (!ord) return false;
    if (
      ord.is_manual ||
      ord.is_manually_created ||
      ord.created_manually ||
      ord.is_manual_order ||
      ord.is_unrecorded ||
      ord.unrecorded
    ) {
      return true;
    }
    const orderSystem = String(ord.order_system || '').toLowerCase();
    const orderSource = String(ord.order_source || ord.source || '').toLowerCase();
    const channel = String(ord.channel || ord.creation_mode || ord.creation_source || '').toLowerCase();
    const recordStatus = String(ord.record_status || '').toLowerCase();

    if (
      orderSystem === 'unrecorded' ||
      orderSystem === 'manual' ||
      orderSource === 'manual' ||
      orderSource === 'unrecorded' ||
      orderSource === 'data_entry' ||
      orderSource === 'admin' ||
      channel === 'manual' ||
      channel === 'unrecorded' ||
      channel === 'admin' ||
      recordStatus === 'unrecorded' ||
      recordStatus === 'unrecorded_order'
    ) {
      return true;
    }

    const orderId = String(ord.order_number || ord.order_id || ord.id || '');
    if (orderId.startsWith('UNREC_') || orderId.startsWith('MANUAL_') || orderId.startsWith('#UNREC_') || orderId.startsWith('#MANUAL_')) {
      return true;
    }

    if (ord.created_by_name || ord.staff_name || ord.admin_name || ord.creator_name || (ord.created_by && typeof ord.created_by === 'object')) {
      return true;
    }

    return false;
  };



  const [orders, setOrders] = useState([]);

  const [loading, setLoading] = useState(true);

  const [error, setError] = useState(null);

  const [activeTab, setActiveTab] = useState(() => localStorage.getItem('orderManagement_activeTab') || 'online');

  // Audio notification state
  const [audioEnabled, setAudioEnabled] = useState(true);
  const audioRef = useRef(null);
  const previousOrderCount = useRef(0);
  const ordersFingerprint = useRef('');
  const fetchIdRef = useRef(0);
  const isInitialLoadRef = useRef(true);
  const previousOrderIdsRef = useRef(new Set());
  const previousOrderPaymentStatusesRef = useRef(new Map());
  const audioUnlockedRef = useRef(false);

  // Unlock browser audio permissions on first user interaction
  useEffect(() => {
    const unlockAudio = () => {
      if (audioUnlockedRef.current) return;
      if (audioRef.current) {
        audioRef.current.play()
          .then(() => {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
            audioUnlockedRef.current = true;
          })
          .catch(() => { });
      }
    };

    window.addEventListener('click', unlockAudio, { once: true });
    window.addEventListener('touchstart', unlockAudio, { once: true });
    window.addEventListener('keydown', unlockAudio, { once: true });

    return () => {
      window.removeEventListener('click', unlockAudio);
      window.removeEventListener('touchstart', unlockAudio);
      window.removeEventListener('keydown', unlockAudio);
    };
  }, []);


  // Load saved page from localStorage on initial load
  const savedPage = typeof window !== 'undefined' ?
    parseInt(localStorage.getItem('orderManagement_currentPage')) || 1 : 1;

  const today = new Date();
  const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const pad = n => String(n).padStart(2, '0');
  const toDateStr = `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`;
  const fromDateStr = `${firstOfMonth.getFullYear()}-${pad(firstOfMonth.getMonth() + 1)}-${pad(firstOfMonth.getDate())}`;

  const [filters, setFilters] = useState({
    status: '',
    order_type: '',
    business_id: '',
    search: '',
    source: '',
    date_from: fromDateStr,
    date_to: toDateStr,
    payment_status: '',
    page: savedPage,
    limit: 10
  });

  const [searchInput, setSearchInput] = useState('');
  const [dateFromInput, setDateFromInput] = useState(fromDateStr);
  const [dateToInput, setDateToInput] = useState(toDateStr);

  const [pagination, setPagination] = useState({});

  const [selectedOrder, setSelectedOrder] = useState(null);
  const [kpiMetrics, setKpiMetrics] = useState(null);

  const [showStatusModal, setShowStatusModal] = useState(false);

  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showGstBreakdown, setShowGstBreakdown] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  const [audioDelayTimer, setAudioDelayTimer] = useState(null);



  // Debug modal state changes - SEPARATE effects to avoid interference

  useEffect(() => {

    if (showStatusModal) {

      document.body.style.overflow = 'hidden';

    } else {

      if (!showDetailsModal) {

        document.body.style.overflow = '';

      }

    }



    return () => {

      if (!showDetailsModal && !showStatusModal) {

        document.body.style.overflow = '';

      }

    };

  }, [showStatusModal]); // Removed selectedOrder dependency



  // Separate effect for details modal

  useEffect(() => {

    if (showDetailsModal) {


      document.body.style.overflow = 'hidden';

    } else {


      if (!showStatusModal) {

        document.body.style.overflow = '';

      }

    }



    return () => {

      if (!showDetailsModal && !showStatusModal) {

        document.body.style.overflow = '';

      }

    };

  }, [showDetailsModal]);



  // Enhanced status update modal states

  const [deliveryPartners, setDeliveryPartners] = useState([]);

  const [selectedDeliveryPartner, setSelectedDeliveryPartner] = useState(null);

  const [loadingPartners, setLoadingPartners] = useState(false);

  const [selectedStatus, setSelectedStatus] = useState('');

  // Modal tab: 'status' or 'payment'
  const [statusModalTab, setStatusModalTab] = useState('status');

  // Payment status update state
  const [selectedPaymentStatusToSet, setSelectedPaymentStatusToSet] = useState('');
  const [paymentUpdating, setPaymentUpdating] = useState(false);


  // Custom dropdown states

  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);

  const [orderTypeDropdownOpen, setOrderTypeDropdownOpen] = useState(false);

  const [paymentStatusDropdownOpen, setPaymentStatusDropdownOpen] = useState(false);

  const [sourceDropdownOpen, setSourceDropdownOpen] = useState(false);

  const [searchTimeout, setSearchTimeout] = useState(null);

  const modalOpenTimeRef = useRef(0);



  // Fetch available delivery partners

  const fetchDeliveryPartners = useCallback(async () => {

    try {

      setLoadingPartners(true);

      const response = await AdminService.getAllDeliveryProviders({

        page: 1,

        limit: 100,

        status: 'Available' // Only fetch available partners

      });

      // Filter client-side as a safeguard in case the backend doesn't support status filtering
      const filtered = (response.providers || []).filter(
        partner => partner.status?.toLowerCase() === 'available'
      );

      setDeliveryPartners(filtered);

    } catch (err) {

      console.error('Failed to fetch delivery partners:', err);

      setDeliveryPartners([]);

    } finally {

      setLoadingPartners(false);

    }

  }, []);



  // Removed sidebar state - now handled by AdminDashboard



  const terminalStatuses = ['delivered', 'completed', 'cancelled', 'rejected'];

  const matchesFilters = (order, filterObj = filters, tab = activeTab) => {
    const fromDate = filterObj.date_from ? new Date(filterObj.date_from + 'T00:00:00') : null;
    const toDate = filterObj.date_to ? new Date(filterObj.date_to + 'T23:59:59') : null;

    if (tab === 'online') {
      if (order.scheduled_time && new Date(order.scheduled_time) > new Date()) return false;
    }

    if (tab === 'scheduled') {
      if (order.scheduled_time && new Date(order.scheduled_time) <= new Date()) return false;
    }

    if ((tab === 'online' || tab === 'scheduled') && !filterObj.status) {
      if (terminalStatuses.includes((order.status || '').toLowerCase())) return false;
    }

    if (filterObj.status) {
      const filterStatus = filterObj.status.toLowerCase().replace(/[-_]/g, ' ').trim();
      const orderStatus = (order.status || '').toLowerCase().replace(/[-_]/g, ' ').trim();
      if (orderStatus !== filterStatus) return false;
    }

    if (filterObj.order_type) {
      const filterType = filterObj.order_type.toLowerCase().replace(/[-_]/g, ' ').trim();
      const orderType = (order.order_type || '').toLowerCase().replace(/[-_]/g, ' ').trim();
      if (filterType.includes('porter')) {
        if (!orderType.includes('porter')) return false;
      } else if (orderType !== filterType) return false;
    }

    if (filterObj.payment_status) {
      const currentPs = (order.payment_status || '').toLowerCase().trim();
      const targetPs = filterObj.payment_status.toLowerCase().trim();
      if (targetPs === 'paid' || targetPs === 'success') {
        if (currentPs !== 'paid' && currentPs !== 'success') return false;
      } else if (targetPs === 'completed') {
        if (currentPs !== 'completed') return false;
      } else if (targetPs === 'pending' || targetPs === 'unpaid') {
        if (currentPs !== 'pending' && currentPs !== 'unpaid') return false;
      } else if (targetPs === 'cod') {
        if (currentPs !== 'cod' && currentPs !== 'pay_on_delivery' && currentPs !== 'cash_on_delivery') return false;
      } else if (currentPs !== targetPs) return false;
    }

    if (filterObj.search && filterObj.search.trim()) {
      const q = filterObj.search.trim().toLowerCase();
      const qNormalized = q.replace(/[-_]/g, ' ');
      const orderIdStr = String(order.order_id || '').toLowerCase();
      const orderNumStr = String(order.order_number || '').toLowerCase();
      const custNameStr = String(order.customer_name || '').toLowerCase();
      const custPhoneStr = String(order.customer_phone || '').toLowerCase();
      const bizNameStr = String(order.business_name || '').toLowerCase();
      const partnerNameStr = String(order.delivery_partner_name || '').toLowerCase();
      const orderTypeStr = String(order.order_type || '').toLowerCase();
      const orderTypeNormalized = orderTypeStr.replace(/[-_]/g, ' ');

      const matchesSearch =
        orderIdStr.includes(q) ||
        orderNumStr.includes(q) ||
        custNameStr.includes(q) ||
        custPhoneStr.includes(q) ||
        bizNameStr.includes(q) ||
        partnerNameStr.includes(q) ||
        orderTypeStr.includes(q) ||
        orderTypeNormalized.includes(qNormalized);

      if (!matchesSearch) return false;
    }

    if (filterObj.source && filterObj.source.trim()) {
      const srcFilter = filterObj.source.trim().toLowerCase();
      const orderSource = String(
        order?.source ||
        order?.order_source ||
        order?.source_type ||
        order?.payment_source ||
        order?.payment_details?.payment_source ||
        order?.channel ||
        ''
      ).trim().toLowerCase();

      if (!orderSource.includes(srcFilter) && orderSource !== srcFilter) return false;
    }

    if (fromDate || toDate) {
      const orderDate = new Date(order.created_at);
      if (fromDate && orderDate < fromDate) return false;
      if (toDate && orderDate > toDate) return false;
    }

    return true;
  };

  const fetchOrders = useCallback(async (silent = false) => {
    const currentFetchId = ++fetchIdRef.current;

    try {
      if (!silent) {
        setLoading(true);
      }
      setError(null);

      const { payment_status, ...apiFilters } = filters;
      const requestFilters = {
        ...apiFilters,
        page: 1,
        limit: 200,
        source: filters.source || undefined,
        payment_source: filters.source || undefined
      };

      let firstRes;
      if (activeTab === 'counter') {
        firstRes = await AdminService.getCounterOrders({ ...requestFilters, status_group: 'ongoing' });
      } else if (activeTab === 'scheduled') {
        firstRes = await AdminService.getAllOrders({ ...requestFilters, scheduled: 'true' });
      } else if (activeTab === 'campuslife') {
        firstRes = await AdminService.getCampusLifeOrders(requestFilters);
      } else {
        firstRes = await AdminService.getAllOrders(requestFilters);
      }

      if (currentFetchId !== fetchIdRef.current) return;

      let rawOrders = firstRes?.orders || firstRes?.results || firstRes?.data?.orders || firstRes?.data?.results || [];
      if (activeTab === 'counter') {
        rawOrders = rawOrders.map(o => ({ ...o, order_system: 'counter' }));
      } else if (activeTab === 'campuslife') {
        rawOrders = rawOrders.map(o => ({ ...o, order_system: 'campuslife' }));
      }
      let collected = [...rawOrders];
      const backendTotalPages = firstRes?.pagination?.total_pages || firstRes?.pages || firstRes?.data?.pagination?.total_pages || 1;

      if (firstRes?.kpi_metrics) {
        setKpiMetrics(firstRes.kpi_metrics);
      }

      if (backendTotalPages > 1) {
        const pageNumbers = Array.from({ length: backendTotalPages - 1 }, (_, i) => i + 2);
        const restResults = await Promise.all(
          pageNumbers.map(p => {
            const pageFilters = { ...requestFilters, page: p };
            if (activeTab === 'counter') {
              return AdminService.getCounterOrders({ ...pageFilters, status_group: 'ongoing' });
            } else if (activeTab === 'scheduled') {
              return AdminService.getAllOrders({ ...pageFilters, scheduled: 'true' });
            } else if (activeTab === 'campuslife') {
              return AdminService.getCampusLifeOrders(pageFilters);
            } else {
              return AdminService.getAllOrders(pageFilters);
            }
          })
        );
        for (const r of restResults) {
          let pOrders = r?.orders || r?.results || r?.data?.orders || r?.data?.results || [];
          if (activeTab === 'counter') {
            pOrders = pOrders.map(o => ({ ...o, order_system: 'counter' }));
          } else if (activeTab === 'campuslife') {
            pOrders = pOrders.map(o => ({ ...o, order_system: 'campuslife' }));
          }
          collected = collected.concat(pOrders);
        }
      }

      // De-duplicate orders by order_system + order_id
      const seenKeys = new Set();
      collected = collected.filter(o => {
        const key = `${o.order_system || 'standard'}-${o.order_id || o.id}`;
        if (seenKeys.has(key)) return false;
        seenKeys.add(key);
        return true;
      });

      // Filter ALL collected orders across all pages using matchesFilters
      const allFilteredOrders = collected.filter(o => matchesFilters(o, filters, activeTab));

      const newFingerprint = allFilteredOrders
        .map(o => `${o.order_id || o.id}:${o.status}:${o.payment_status || ''}`)
        .join('|');

      const PAGE_SIZE = 10;
      const totalOrderCount = allFilteredOrders.length;
      const totalPages = Math.max(1, Math.ceil(totalOrderCount / PAGE_SIZE));
      const currentPage = Math.min(filters.page || 1, totalPages);

      const effectivePagination = {
        total_orders: totalOrderCount,
        total_pages: totalPages,
        current_page: currentPage,
        per_page: PAGE_SIZE
      };

      // Check for new orders with successful payment status
      const isSuccessfulPayment = (ps) => {
        const s = (ps || '').toLowerCase().trim();
        return s === 'success' || s === 'paid' || s === 'completed';
      };

      const currentOrderMap = new Map();
      allFilteredOrders.forEach(o => {
        const key = `${o.order_system || 'standard'}-${o.order_id || o.id}`;
        currentOrderMap.set(key, o.payment_status || '');
      });

      if (isInitialLoadRef.current) {
        isInitialLoadRef.current = false;
      } else {
        let hasNewSuccessPayment = false;
        for (const [key, currentPs] of currentOrderMap.entries()) {
          const prevPs = previousOrderPaymentStatusesRef.current.get(key);
          const isNowSuccess = isSuccessfulPayment(currentPs);

          // Trigger audio if it is a new order with successful payment,
          // OR if an existing order transitioned to successful payment
          if (isNowSuccess && (prevPs === undefined || !isSuccessfulPayment(prevPs))) {
            hasNewSuccessPayment = true;
            break;
          }
        }

        if (hasNewSuccessPayment) {
          console.log('🔔 New order with successful payment detected! Triggering audio notification...');
          if (audioEnabled) {
            playAudioTwice();
          }
        }
      }

      previousOrderPaymentStatusesRef.current = currentOrderMap;
      previousOrderIdsRef.current = new Set(currentOrderMap.keys());
      ordersFingerprint.current = newFingerprint;
      setOrders(allFilteredOrders);
      setPagination(effectivePagination);

      previousOrderCount.current = totalOrderCount;

    } catch (err) {
      if (currentFetchId !== fetchIdRef.current) return;
      setError('Failed to fetch orders');
      console.error('Orders fetch error:', err);
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [filters, activeTab, audioEnabled]);

  // Audio playback function that plays twice with fallback
  const playAudioTwice = useCallback(() => {
    if (!audioEnabled) return;

    const playFirstAudio = () => {
      let audioElement1 = audioRef.current;

      if (!audioElement1) {
        audioElement1 = new Audio(newOrderSound);
        audioElement1.preload = 'auto';
      }

      if (audioElement1) {
        audioElement1.currentTime = 0;
        return audioElement1.play()
          .then(() => true)
          .catch(err => {
            console.warn('❌ OrderManagement - First audio play failed:', err);
            try {
              const freshAudio = new Audio(newOrderSound);
              return freshAudio.play().then(() => true).catch(() => false);
            } catch (e) {
              return false;
            }
          });
      } else {
        console.warn('❌ OrderManagement - Could not create audio element 1');
        return Promise.resolve(false);
      }
    };

    const playSecondAudio = () => {
      const audioElement2 = new Audio(newOrderSound);
      audioElement2.preload = 'auto';

      return audioElement2.play()
        .then(() => true)
        .catch(err => {
          console.warn('❌ OrderManagement - Second audio play failed:', err);
          return false;
        });
    };

    playFirstAudio().then(success => {
      if (success) {
        if (audioDelayTimer) clearTimeout(audioDelayTimer);
        const timer = setTimeout(() => {
          playSecondAudio();
        }, 5000);
        setAudioDelayTimer(timer);
      }
    });
  }, [audioEnabled, audioDelayTimer]);



  useEffect(() => {

    fetchOrders();

  }, [fetchOrders]);

  // Auto-refresh every 20 seconds
  useEffect(() => {

    const interval = setInterval(() => {
      // Silent refresh - no loading state, no visual indicator
      fetchOrders(true);
    }, 20000);

    return () => {
      clearInterval(interval);
      // Also clear audio delay timer
      if (audioDelayTimer) {
        clearTimeout(audioDelayTimer);
      }
    };
  }, [fetchOrders]);

  // Cleanup audio delay timer on unmount
  useEffect(() => {
    return () => {
      if (audioDelayTimer) {
        clearTimeout(audioDelayTimer);
      }
    };
  }, [audioDelayTimer]);



  // Close dropdowns when clicking outside

  useEffect(() => {

    const handleClickOutside = (event) => {

      if (statusDropdownOpen && !event.target.closest('.status-dropdown')) {

        setStatusDropdownOpen(false);

      }

      if (orderTypeDropdownOpen && !event.target.closest('.order-type-dropdown')) {

        setOrderTypeDropdownOpen(false);

      }

      if (paymentStatusDropdownOpen && !event.target.closest('.payment-status-dropdown')) {

        setPaymentStatusDropdownOpen(false);

      }

    };



    document.addEventListener('mousedown', handleClickOutside);

    return () => {

      document.removeEventListener('mousedown', handleClickOutside);

    };

  }, [statusDropdownOpen, orderTypeDropdownOpen, paymentStatusDropdownOpen]);



  // Cleanup search timeout on unmount

  useEffect(() => {

    return () => {

      if (searchTimeout) {

        clearTimeout(searchTimeout);

      }

    };

  }, [searchTimeout]);



  // Replaced duplicate useEffect for fetching delivery partners and moved its condition fully to handleOpenStatusModal.



  // Removed debug useEffect to prevent extra re-renders



  // Debug showDetailsModal changes - MOVED ABOVE as separate effect



  const handleFilterChange = (key, value) => {
    const newPage = key === 'page' ? value : 1;
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: newPage
    }));
    if (typeof window !== 'undefined') {
      localStorage.setItem('orderManagement_currentPage', newPage.toString());
    }
  };

  const handleDateChange = (key, val) => {
    if (key === 'date_from') setDateFromInput(val);
    if (key === 'date_to') setDateToInput(val);

    if (!val) {
      handleFilterChange(key, '');
      return;
    }

    if (/^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/.test(val)) {
      const year = parseInt(val.split('-')[0], 10);
      if (year >= 1900 && year <= 2100) {
        handleFilterChange(key, val);
      }
    }
  };

  const handleDateKeyDown = (e, key) => {
    if (e.key === 'Enter') {
      const val = key === 'date_from' ? dateFromInput : dateToInput;
      if (!val || /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/.test(val)) {
        handleFilterChange(key, val);
      }
    }
  };



  const handlePageChange = (newPage) => {

    if (newPage >= 1 && newPage <= pagination.total_pages) {

      setFilters(prev => ({

        ...prev,

        page: newPage

      }));

      // Save current page to localStorage
      if (typeof window !== 'undefined') {
        localStorage.setItem('orderManagement_currentPage', newPage.toString());
      }

    }

  };



  const handleSearchChange = (value) => {

    // Clear existing timeout

    if (searchTimeout) {

      clearTimeout(searchTimeout);

    }



    // Set new timeout for debounced search

    const newTimeout = setTimeout(() => {

      handleFilterChange('business_id', value);

    }, 300); // 300ms delay



    setSearchTimeout(newTimeout);

  };










  // Open status update modal and fetch delivery partners

  const handleOpenStatusModal = useCallback((order) => {

    // Record modal open time

    modalOpenTimeRef.current = Date.now();



    // Batch state updates (React 18 automatically batches these)

    setSelectedOrder(order);

    setSelectedStatus('');

    setSelectedDeliveryPartner(null);

    setSelectedPaymentStatusToSet('');

    setStatusModalTab('status');

    setShowStatusModal(true);


    // Fetch delivery partners to get the latest available list
    fetchDeliveryPartners();

  }, [fetchDeliveryPartners]);



  const normalizeBusinessData = (sourceOrder = {}, fallbackOrder = {}) => {
    const rawBusiness = sourceOrder.business || fallbackOrder.business || sourceOrder.business_details || fallbackOrder.business_details || {};
    const business = typeof rawBusiness === 'object' ? rawBusiness : {};
    const businessName = sourceOrder.business_name || fallbackOrder.business_name || business.business_name || business.name || null;
    const businessPhone = sourceOrder.business_phone || sourceOrder.contact_mobile || sourceOrder.business_contact?.business_number || business.business_phone || business.contact_mobile || business.phone || business.businessNumber || null;
    const businessAddress = sourceOrder.business_address || business.business_address || business.address || sourceOrder.address || null;

    const normalizedBusinessDetails = {
      ...business,
      business_id: sourceOrder.business_id || fallbackOrder.business_id || business.business_id || null,
      business_name: businessName,
      business_number: businessPhone,
      business_phone: businessPhone,
      phone: businessPhone,
      address: businessAddress,
      business_address: businessAddress,
      city: business.city || sourceOrder.city || null,
      state: business.state || sourceOrder.state || null,
      pincode: business.pincode || sourceOrder.pincode || null,
      landmark: business.landmark || sourceOrder.landmark || null,
      location: business.location || sourceOrder.location || null
    };

    return {
      business: business,
      business_name: businessName,
      business_id: sourceOrder.business_id || fallbackOrder.business_id || business.business_id || null,
      business_phone: businessPhone,
      business_details: normalizedBusinessDetails,
      business_contact: {
        business_number: businessPhone
      }
    };
  };

  // Open order details modal

  const handleOpenDetailsModal = async (order) => {

    try {


      // Fetch full order details including items from the API

      const orderSystem = order.order_system || (activeTab === 'counter' ? 'counter' : (activeTab === 'campuslife' ? 'campuslife' : 'standard'));
      const response = await AdminService.getOrderDetails(order.order_id, orderSystem, order);




      // API returns {success: true, data: {order: {...}}} or {success: true, data: {...}} for counter

      const orderData = response.data?.order || (response.data && !response.data.order ? response.data : null) || response.order;



      // Check if response has error indicators

      const hasErrorInResponse = !response.success ||

        response.error ||

        (response.message && response.message.includes('Error'));



      if (response.success && orderData) {


        // Format delivery address if it's an object or string
        let formattedDeliveryAddress = '';
        const rawAddrSource = orderData.delivery_address || orderData.delivery_address_details?.formatted_address || order.delivery_address;
        if (rawAddrSource) {
          if (typeof rawAddrSource === 'object') {
            const addr = rawAddrSource;
            const parts = [
              addr.door_no || addr.doorNo || addr.flat_no || addr.flatNo || addr.door,
              addr.street || addr.area || addr.locality,
              addr.landmark ? `Near ${addr.landmark}` : null,
              addr.city,
              addr.state,
              addr.pincode || addr.pin_code || addr.pin,
              addr.country
            ].filter(Boolean);
            formattedDeliveryAddress = addr.address || addr.full_address || addr.formatted_address || (parts.length > 0 ? parts.join(', ') : '');
          } else if (typeof rawAddrSource === 'string') {
            try {
              const parsed = JSON.parse(rawAddrSource);
              if (parsed && typeof parsed === 'object') {
                const parts = [
                  parsed.door_no || parsed.doorNo || parsed.flat_no || parsed.flatNo || parsed.door,
                  parsed.street || parsed.area || parsed.locality,
                  parsed.landmark ? `Near ${parsed.landmark}` : null,
                  parsed.city,
                  parsed.state,
                  parsed.pincode || parsed.pin_code || parsed.pin,
                  parsed.country
                ].filter(Boolean);
                formattedDeliveryAddress = parsed.address || parsed.full_address || parsed.formatted_address || (parts.length > 0 ? parts.join(', ') : '');
              } else {
                formattedDeliveryAddress = rawAddrSource;
              }
            } catch (_) {
              formattedDeliveryAddress = rawAddrSource;
            }
          }
        }

        // Normalise items structure
        let normalizedItems = [];
        if (orderData.items) {
          normalizedItems = orderData.items.map(item => {
            const rawName = item.product_name || item.name || item.item_name || 'Item';
            const rawVariant = item.variant_name || item.size_label || item.variant_size || (typeof item.variant === 'string' ? item.variant : item.variant?.name) || (item.variant_details?.variant_name) || '';
            const variantStr = typeof rawVariant === 'object' && rawVariant !== null
              ? (rawVariant.variant_name || rawVariant.name || rawVariant.size_label || rawVariant.value || '')
              : String(rawVariant || '').trim();
            const qty = Number(item.quantity || item.qty || 1);
            const unitPrice = Number(item.unit_price || item.price || 0);
            const totalPrice = Number(item.total_price || item.sub_total || (unitPrice * qty) || 0);

            const rawImg = item.image || item.item_image || item.product_image || item.main_image || null;
            const formattedImg = rawImg && typeof rawImg === 'string' && rawImg.trim() && rawImg !== 'null' && rawImg !== 'undefined'
              ? (rawImg.startsWith('http://') || rawImg.startsWith('https://')
                  ? rawImg
                  : `https://kirazee-bucket.s3.ap-south-1.amazonaws.com/dev/media/${rawImg.replace(/^\/?(media\/)?/, '')}`)
              : null;

            return {
              ...item,
              name: rawName,
              item_name: rawName,
              product_name: rawName,
              variant_name: variantStr,
              size_label: variantStr,
              variant_size: variantStr,
              quantity: qty,
              unit_price: unitPrice,
              total_price: totalPrice,
              image: formattedImg,
              item_image: formattedImg,
              main_image: formattedImg,
              product_image: formattedImg
            };
          });
        }

        // Normalise summary
        const summary = {
          subtotal: (orderData.total_amount || 0) - (orderData.delivery_charges || 0) - (orderData.parcel_charges || 0) - (orderData.gst_amount || 0) + (orderData.discount || 0),
          delivery_charges: orderData.delivery_charges || 0,
          parcel_charges: orderData.parcel_charges || 0,
          tax: orderData.gst_amount || 0,
          total: orderData.final_amount || orderData.total_amount || 0
        };

        const normalizedBusiness = normalizeBusinessData(orderData, order);

        // Extract customer name & phone prioritising delivery_address object details (e.g. name, mobile, phone, customer_name)
        const addrObj = (orderData.delivery_address && typeof orderData.delivery_address === 'object') ? orderData.delivery_address : {};
        const deliveryCustName = addrObj.name || addrObj.customer_name || addrObj.receiver_name || addrObj.recipient_name;
        const deliveryCustPhone = addrObj.mobile || addrObj.phone || addrObj.customer_phone || addrObj.receiver_phone || addrObj.contact_number;

        const effectiveCustName = deliveryCustName || orderData.customer_name || (orderData.first_name ? `${orderData.first_name} ${orderData.last_name || ''}`.trim() : order.customer_name);
        const effectiveCustPhone = deliveryCustPhone || orderData.customer_phone || orderData.phone_number || order.customer_phone || '';

        // Capture who originally created/placed the order if created manually
        const isManual = isManualOrder(orderData) || isManualOrder(order);
        const manualCreatorObj = (orderData.created_by && typeof orderData.created_by === 'object') ? orderData.created_by : ((order.created_by && typeof order.created_by === 'object') ? order.created_by : (orderData.ordered_by && typeof orderData.ordered_by === 'object' ? orderData.ordered_by : null));

        const orderedByName = isManual
          ? (manualCreatorObj?.name || manualCreatorObj?.username || orderData.created_by_name || orderData.staff_name || orderData.admin_name || orderData.creator_name || orderData.customer_name || (orderData.first_name ? `${orderData.first_name} ${orderData.last_name || ''}`.trim() : '') || order.customer_name)
          : (manualCreatorObj?.name || orderData.created_by_name || orderData.staff_name || null);

        const orderedByPhone = isManual
          ? (manualCreatorObj?.phone || manualCreatorObj?.mobile || orderData.created_by_phone || orderData.staff_phone || orderData.customer_phone || orderData.phone_number || order.customer_phone)
          : (manualCreatorObj?.phone || manualCreatorObj?.mobile || orderData.created_by_phone || orderData.staff_phone || null);

        // Merge API data with original order data to preserve delivery partner and payment info
        const mergedOrderData = {
          ...orderData,
          business: orderData.business || normalizedBusiness.business,
          business_name: orderData.business_name || normalizedBusiness.business_name || order.business_name || 'N/A',
          business_id: orderData.business_id || normalizedBusiness.business_id || order.business_id || null,
          business_details: orderData.business_details || normalizedBusiness.business_details,
          business_contact: orderData.business_contact || normalizedBusiness.business_contact,
          delivery_address: formattedDeliveryAddress || (typeof order.delivery_address === 'string' ? order.delivery_address : (order.delivery_address?.address || order.delivery_address?.full_address || 'N/A')),
          delivery_address_details: orderData.delivery_address_details || (formattedDeliveryAddress ? { formatted_address: formattedDeliveryAddress } : null) || order.delivery_address_details,
          items: normalizedItems,
          summary: orderData.summary || summary,
          customer_name: effectiveCustName,
          customer_phone: effectiveCustPhone,
          ordered_by_name: orderedByName,
          ordered_by_phone: orderedByPhone,
          customer: {
            ...orderData.customer,
            display_name: effectiveCustName,
            name: effectiveCustName,
            phone: effectiveCustPhone,
            id: orderData.user_id || orderData.customer?.id || order.user_id
          },
          // Preserve delivery partner fields from original order if they exist
          delivery_partner_name: orderData.delivery_partner_name || order.delivery_partner_name,
          delivery_partner_phone: orderData.delivery_partner_phone || order.delivery_partner_phone,
          // Always prioritize original order payment status to avoid COD override
          payment_status: order.payment_status ?? orderData.payment_status,
          // Rejection & Refund details
          is_refund_completed: orderData.is_refund_completed ?? response.data?.is_refund_completed ?? response.is_refund_completed ?? order.is_refund_completed,
          refund_amount: orderData.refund_amount ?? response.data?.refund_amount ?? response.refund_amount ?? order.refund_amount,
          refund_status: orderData.refund_status ?? response.data?.refund_status ?? response.refund_status ?? order.refund_status,
          rejected_by: orderData.rejected_by ?? response.data?.rejected_by ?? response.rejected_by ?? order.rejected_by,
          rejection_reason: orderData.rejection_reason ?? response.data?.rejection_reason ?? response.rejection_reason ?? order.rejection_reason,
          rejected_at: orderData.rejected_at ?? response.data?.rejected_at ?? response.rejected_at ?? order.rejected_at,
          refund_details: orderData.refund_details || response.data?.refund_details || response.refund_details || order.refund_details,
          porter_details: orderData.porter_details || response.data?.porter_details || response.porter_details || order.porter_details || null
        };

        setSelectedOrder(mergedOrderData);

        modalOpenTimeRef.current = Date.now();

        setShowDetailsModal(true);
        setShowGstBreakdown(false);

      } else if (hasErrorInResponse) {

        // Backend returned error response - extract error message

        const backendErrorMsg = response.message || response.error || 'Unknown backend error';

        throw new Error(backendErrorMsg);

      } else {

        throw new Error('Failed to fetch order details');

      }



    } catch (err) {

      console.error('Failed to open order details:', err);



      // ALWAYS fallback to table data when API fails

      // This ensures users can still see basic order info even if backend has errors


      const normalizedFallbackBusiness = normalizeBusinessData(order);
      const isManualFallback = isManualOrder(order);
      const manualCreatorFallback = (order.created_by && typeof order.created_by === 'object') ? order.created_by : (order.ordered_by && typeof order.ordered_by === 'object' ? order.ordered_by : null);
      const fallbackOrderedByName = isManualFallback
        ? (manualCreatorFallback?.name || manualCreatorFallback?.username || order.created_by_name || order.staff_name || order.admin_name || order.creator_name || order.customer_name)
        : (manualCreatorFallback?.name || order.created_by_name || order.staff_name || null);
      const fallbackOrderedByPhone = isManualFallback
        ? (manualCreatorFallback?.phone || manualCreatorFallback?.mobile || order.created_by_phone || order.staff_phone || order.customer_phone || order.phone_number)
        : (manualCreatorFallback?.phone || manualCreatorFallback?.mobile || order.created_by_phone || order.staff_phone || null);

      // Format fallback delivery address
      let fallbackDeliveryAddress = '';
      const fallbackAddrSrc = order.delivery_address || order.delivery_address_details?.formatted_address;
      if (fallbackAddrSrc) {
        if (typeof fallbackAddrSrc === 'object') {
          const addr = fallbackAddrSrc;
          const parts = [
            addr.door_no || addr.doorNo || addr.flat_no || addr.flatNo || addr.door,
            addr.street || addr.area || addr.locality,
            addr.landmark ? `Near ${addr.landmark}` : null,
            addr.city,
            addr.state,
            addr.pincode || addr.pin_code || addr.pin,
            addr.country
          ].filter(Boolean);
          fallbackDeliveryAddress = addr.address || addr.full_address || addr.formatted_address || (parts.length > 0 ? parts.join(', ') : '');
        } else if (typeof fallbackAddrSrc === 'string') {
          try {
            const parsed = JSON.parse(fallbackAddrSrc);
            if (parsed && typeof parsed === 'object') {
              const parts = [
                parsed.door_no || parsed.doorNo || parsed.flat_no || parsed.flatNo || parsed.door,
                parsed.street || parsed.area || parsed.locality,
                parsed.landmark ? `Near ${parsed.landmark}` : null,
                parsed.city,
                parsed.state,
                parsed.pincode || parsed.pin_code || parsed.pin,
                parsed.country
              ].filter(Boolean);
              fallbackDeliveryAddress = parsed.address || parsed.full_address || parsed.formatted_address || (parts.length > 0 ? parts.join(', ') : '');
            } else {
              fallbackDeliveryAddress = fallbackAddrSrc;
            }
          } catch (_) {
            fallbackDeliveryAddress = fallbackAddrSrc;
          }
        }
      }

      // Construct minimal order object from table data

      const fallbackOrder = {

        ...order,
        delivery_address: fallbackDeliveryAddress || (typeof order.delivery_address === 'string' ? order.delivery_address : (order.delivery_address?.address || order.delivery_address?.full_address || 'N/A')),
        delivery_address_details: order.delivery_address_details || (fallbackDeliveryAddress ? { formatted_address: fallbackDeliveryAddress } : null),

        // Ensure items is at least an empty array with normalized fields

        items: (order.items || []).map(item => {
          const rawName = item.product_name || item.name || item.item_name || 'Item';
          const rawVariant = item.variant_name || item.size_label || item.variant_size || (typeof item.variant === 'string' ? item.variant : item.variant?.name) || (item.variant_details?.variant_name) || '';
          const variantStr = typeof rawVariant === 'object' && rawVariant !== null
            ? (rawVariant.variant_name || rawVariant.name || rawVariant.size_label || rawVariant.value || '')
            : String(rawVariant || '').trim();
          const qty = Number(item.quantity || item.qty || 1);
          const unitPrice = Number(item.unit_price || item.price || 0);
          const totalPrice = Number(item.total_price || item.sub_total || (unitPrice * qty) || 0);

          const rawImg = item.image || item.item_image || item.product_image || item.main_image || null;
          const formattedImg = rawImg && typeof rawImg === 'string' && rawImg.trim() && rawImg !== 'null' && rawImg !== 'undefined'
            ? (rawImg.startsWith('http://') || rawImg.startsWith('https://')
                ? rawImg
                : `https://kirazee-bucket.s3.ap-south-1.amazonaws.com/dev/media/${rawImg.replace(/^\/?(media\/)?/, '')}`)
            : null;

          return {
            ...item,
            name: rawName,
            item_name: rawName,
            product_name: rawName,
            variant_name: variantStr,
            size_label: variantStr,
            variant_size: variantStr,
            quantity: qty,
            unit_price: unitPrice,
            total_price: totalPrice,
            image: formattedImg,
            item_image: formattedImg,
            main_image: formattedImg,
            product_image: formattedImg
          };
        }),

        // Ensure customer object exists

        customer: order.customer || {

          display_name: order.customer_name,

          phone: order.customer_phone,

          id: order.user_id

        },

        business: order.business || normalizedFallbackBusiness.business,
        business_name: order.business_name || normalizedFallbackBusiness.business_name || 'N/A',
        business_id: order.business_id || normalizedFallbackBusiness.business_id || null,
        business_phone: order.business_phone || normalizedFallbackBusiness.business_phone || null,
        business_contact: order.business_contact || normalizedFallbackBusiness.business_contact,

        // Ensure business_details exists

        business_details: order.business_details || normalizedFallbackBusiness.business_details || {

          business_name: order.business_name,

          business_id: order.business_id

        },

        ordered_by_name: fallbackOrderedByName,
        ordered_by_phone: fallbackOrderedByPhone,

        // Preserve delivery partner information explicitly
        delivery_partner_name: order.delivery_partner_name,
        delivery_partner_phone: order.delivery_partner_phone,
        // Preserve payment status from original order
        payment_status: order.payment_status,
        is_refund_completed: order.is_refund_completed,
        refund_amount: order.refund_amount,
        refund_status: order.refund_status,
        rejected_by: order.rejected_by,
        rejection_reason: order.rejection_reason,
        rejected_at: order.rejected_at,
        refund_details: order.refund_details,
        porter_details: order.porter_details || null,

        // Minimal summary from available data

        summary: order.summary || {

          subtotal: order.total_amount || 0,

          total: order.final_amount || order.total_amount || 0

        }

      };



      setSelectedOrder(fallbackOrder);

      modalOpenTimeRef.current = Date.now();

      setShowDetailsModal(true);
      setShowGstBreakdown(false);



      // Don't set blocking error - let user see the modal with fallback data

    }

  };



  // Handle status selection

  const handleStatusSelection = useCallback((status) => {

    setSelectedStatus(status);

    // Clear delivery partner selection if not assigning

    if (status !== 'assigned' && String(status).toUpperCase() !== 'RIDER_ALLOCATED') {

      setSelectedDeliveryPartner(null);

    }

  }, []);



  // Handle final status update

  const handleStatusUpdate = async () => {

    if (!selectedStatus) {

      setError('Please select a status');

      return;

    }



    // Check if delivery partner is required for assignment

    if ((selectedStatus === 'assigned' || String(selectedStatus).toUpperCase() === 'RIDER_ACCEPTED') && !selectedDeliveryPartner) {

      setError('Please select a delivery partner for assignment');

      return;

    }



    try {
      setUpdatingStatus(true);
      setError(null);

      const orderSystem = selectedOrder.order_system || (activeTab === 'counter' ? 'counter' : (activeTab === 'campuslife' ? 'campuslife' : 'standard'));
      const currentOrderId = (orderSystem === 'campuslife' || (selectedOrder.order_id && String(selectedOrder.order_id).startsWith('CL_')))
        ? selectedOrder.order_id
        : (selectedOrder.id || selectedOrder.order_id);
      if ((selectedStatus === 'assigned' || String(selectedStatus).toUpperCase() === 'RIDER_ACCEPTED') && selectedDeliveryPartner) {

        // Use assignment API if assigning to delivery partner
        // For porter delivery orders, pass RIDER_ACCEPTED as the new status
        const isPorter = selectedOrder.order_type?.toLowerCase().includes('porter');
        await AdminService.assignDeliveryPartner(
          currentOrderId,
          selectedDeliveryPartner.provider_id,
          orderSystem,
          isPorter ? 'RIDER_ACCEPTED' : null
        );

        // Send porter accept notification
        if (isPorter) {
          try {
            const token = localStorage.getItem('admin_token') || sessionStorage.getItem('admin_session');
            await fetch(`${API_ENDPOINTS.ADMIN_BASE}/porter/order/accept/`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                ...(token ? { 'Authorization': `Bearer ${token}` } : {})
              },
              body: JSON.stringify({
                order_id: currentOrderId,
                rider_id: selectedDeliveryPartner.provider_id
              })
            });
          } catch (notifErr) {
            console.error('Failed to send porter notification:', notifErr);
          }
        }

      } else {

        // Use regular status update API
        await AdminService.updateOrderStatus(
          currentOrderId,
          selectedStatus,
          orderSystem
        );

      }



      // Update local state
      // For porter assign, reflect RIDER_ACCEPTED regardless of selectedStatus
      const effectiveStatus = (String(selectedStatus).toUpperCase() === 'RIDER_ACCEPTED' ||
        (selectedStatus === 'assigned' && selectedOrder.order_type?.toLowerCase().includes('porter')))
        ? 'RIDER_ACCEPTED'
        : selectedStatus;

      setOrders(prev => prev.map(order =>
        order.order_id === selectedOrder.order_id
          ? { ...order, status: effectiveStatus, order_status: effectiveStatus }
          : order
      ));



      setShowStatusModal(false);

      setSelectedOrder(null);

      setSelectedStatus('');

      setSelectedDeliveryPartner(null);

    } catch (err) {

      console.error('Status update failed:', err);

      console.error('Error details:', err.response?.data || err.message);

      const errorMessage = err.response?.data?.message || err.message || 'Failed to update order status';

      setError(errorMessage);

    } finally {

      setUpdatingStatus(false);

    }

  };



  // Payment status update handler
  const handlePaymentStatusUpdate = async () => {
    if (!selectedPaymentStatusToSet) {
      setError('Please select a payment status');
      return;
    }

    try {
      setPaymentUpdating(true);
      setError(null);

      const orderSystem = selectedOrder?.order_system || (activeTab === 'counter' ? 'counter' : (activeTab === 'campuslife' ? 'campuslife' : 'standard'));
      await AdminService.updateOrderPaymentStatus(selectedOrder.order_id, selectedPaymentStatusToSet, orderSystem);

      // Update local state to reflect new payment status
      setOrders(prev => prev.map(order =>
        order.order_id === selectedOrder.order_id
          ? { ...order, payment_status: selectedPaymentStatusToSet }
          : order
      ));

      setSelectedOrder(prev => prev ? { ...prev, payment_status: selectedPaymentStatusToSet } : prev);

      setShowStatusModal(false);
      setSelectedPaymentStatusToSet('');
    } catch (err) {
      console.error('Payment status update failed:', err);
      const errorMessage = err.message || err.response?.data?.message || 'Failed to update payment status';
      setError(errorMessage);
    } finally {
      setPaymentUpdating(false);
    }
  };

  const getStatusColor = (status) => {

    return AdminService.getStatusColor(status);

  };



  const formatCurrency = (amount) => {

    return AdminService.formatCurrency(amount);

  };



  const formatDate = (dateString) => {

    return AdminService.formatDate(dateString);

  };



  const formatTime = (dateString) => {

    if (!dateString) return 'N/A';

    const date = new Date(dateString);

    return date.toLocaleTimeString('en-US', {

      hour: '2-digit',


      minute: '2-digit',
      second: '2-digit',
      hour12: true

    });

  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '';
    const d = new Date(dateString);
    const datePart = d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
    const timePart = d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true });
    return `${datePart} ${timePart}`;
  };




  const formatTimelineTimestamp = (dateString) => {
    if (!dateString) return '';
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return `[${dateString}]`;
    const datePart = d.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' });
    const timePart = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', second: '2-digit', hour12: true });
    return `[${datePart}, ${timePart}]`;
  };

  const formatStatusTimelineForExcel = (order) => {
    if (!Array.isArray(order?.status_timeline) || order.status_timeline.length === 0) {
      return 'N/A';
    }
    return order.status_timeline.map(st => {
      const statusLabel = st.label || st.status || '';
      const timeStr = st.timestamp ? formatTimelineTimestamp(st.timestamp) : '';
      const byStr = st.changed_by || st.source ? ` (By: ${st.changed_by || st.source})` : '';
      const notesStr = st.notes ? ` - Notes: ${st.notes}` : '';
      return `${statusLabel}${timeStr ? ' ' + timeStr : ''}${byStr}${notesStr}`;
    }).join('\n');
  };

  const handleExportExcel = async () => {
    if (exportLoading) return;
    setExportLoading(true);

    try {
      const requestFilters = {
        status: undefined,
        payment_status: undefined,
        order_type: undefined,
        payment_source: filters.source || undefined
      };

      let firstRes;
      if (activeTab === 'counter') {
        firstRes = await AdminService.getCounterOrders({ ...requestFilters, status_group: 'ongoing', page: 1, limit: 100 });
      } else if (activeTab === 'scheduled') {
        firstRes = await AdminService.getAllOrders({ ...requestFilters, scheduled: 'true', page: 1, limit: 100 });
      } else if (activeTab === 'campuslife') {
        firstRes = await AdminService.getCampusLifeOrders({ ...requestFilters, page: 1, limit: 100 });
      } else {
        firstRes = await AdminService.getAllOrders({ ...requestFilters, page: 1, limit: 100 });
      }

      let allFetched = firstRes.orders || [];
      const totalPages = firstRes.pagination?.total_pages || 1;

      if (totalPages > 1) {
        const remainingPages = await Promise.all(
          Array.from({ length: totalPages - 1 }, (_, i) => i + 2).map(p => {
            if (activeTab === 'counter') {
              return AdminService.getCounterOrders({ ...requestFilters, status_group: 'ongoing', page: p, limit: 100 });
            } else if (activeTab === 'scheduled') {
              return AdminService.getAllOrders({ ...requestFilters, scheduled: 'true', page: p, limit: 100 });
            } else if (activeTab === 'campuslife') {
              return AdminService.getCampusLifeOrders({ ...requestFilters, page: p, limit: 100 });
            } else {
              return AdminService.getAllOrders({ ...requestFilters, page: p, limit: 100 });
            }
          })
        );
        remainingPages.forEach(res => {
          if (res.orders) allFetched = allFetched.concat(res.orders);
        });
      }

      // Filter all fetched orders using matchesFilters (which applies date_from / date_to & active filters)
      const filteredForExport = allFetched.filter(order => matchesFilters(order));

      if (filteredForExport.length === 0) {
        alert('No orders match the selected filters/date range to export.');
        return;
      }

      // Sort newest first
      filteredForExport.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));

      // Enrich orders with status_timeline in batches
      const enrichedAllOrders = [];
      const BATCH_SIZE = 15;
      for (let i = 0; i < filteredForExport.length; i += BATCH_SIZE) {
        const batch = filteredForExport.slice(i, i + BATCH_SIZE);
        const batchEnriched = await Promise.all(
          batch.map(async (order) => {
            if (Array.isArray(order.status_timeline) && order.status_timeline.length > 0) {
              return order;
            }
            try {
              const res = await AdminService.getOrderDetails(order.order_id, order.order_system || 'standard');
              const details = res.data?.order || (res.data && !res.data.order ? res.data : null) || res.order || res.data;
              if (details && Array.isArray(details.status_timeline) && details.status_timeline.length > 0) {
                return { ...order, status_timeline: details.status_timeline };
              }
            } catch (e) {
              console.error(`Failed to enrich timeline for order #${order.order_id}:`, e);
            }
            return order;
          })
        );
        enrichedAllOrders.push(...batchEnriched);
      }

      const rows = enrichedAllOrders.map(order => ({
        'Order #': `#${order.order_id}`,
        'Order Number': order.order_number || '',
        'Customer Name': order.customer_name || '',
        'Customer Phone': order.customer_phone || '',
        'Business': order.business_name || '',
        'Order Type': order.order_type || '',
        'Amount (₹)': order.final_amount ?? order.total_amount ?? '',
        'Order Status': getStatusDisplay(order),
        'Payment Status': (order.payment_status || 'PENDING').toUpperCase(),
        'Payment Source': (order.payment_source || order.payment_details?.payment_source || order.source || '').toUpperCase(),
        'Delivery Partner': order.delivery_partner_name || 'Not Assigned',
        'Status Timeline': formatStatusTimelineForExcel(order),
        'Date': formatDate(order.created_at),
      }));

      const worksheet = XLSX.utils.json_to_sheet(rows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Orders');

      // Auto-width columns
      const colWidths = Object.keys(rows[0]).map(key => ({
        wch: Math.min(
          Math.max(
            key.length,
            ...rows.map(r => {
              const val = String(r[key] ?? '');
              return Math.max(...val.split('\n').map(l => l.length));
            })
          ) + 2,
          65
        )
      }));
      worksheet['!cols'] = colWidths;

      const parts = ['orders'];
      if (filters.status) parts.push(filters.status);
      if (filters.order_type) parts.push(filters.order_type);
      if (filters.date_from) parts.push(`from-${filters.date_from}`);
      if (filters.date_to) parts.push(`to-${filters.date_to}`);
      const filename = `${parts.join('_')}.xlsx`;

      XLSX.writeFile(workbook, filename);
    } catch (err) {
      console.error('Export failed:', err);
    } finally {
      setExportLoading(false);
    }
  };

  const statusOptions = [
    'pending', 'confirmed', 'preparing', 'assigned', 'ready',
    'picked_up', 'travelling', 'out_for_delivery', 'delivered', 'completed', 'cancelled', 'rejected'
  ];

  const [porterStatusOptions, setPorterStatusOptions] = useState([
    'SEARCHING_RIDER', 'RIDER_ALLOCATED', 'RIDER_ACCEPTED', 'ON_THE_WAY_TO_PICKUP',
    'AT_PICKUP', 'PICKED_UP', 'IN_TRANSIT', 'AT_DELIVERY', 'DELIVERED', 'COMPLETED'
  ]);

  useEffect(() => {
    const fetchPorterStatuses = async () => {
      try {
        const response = await AdminService.getPorterStatuses();
        if (response.success && response.data) {
          const excludeList = ['cancelled', 'failed'];
          const filteredCodes = response.data
            .map(s => s.status_code)
            .filter(code => !excludeList.includes(String(code).toLowerCase()));
          ['DELIVERED', 'COMPLETED'].forEach(st => {
            if (!filteredCodes.some(c => String(c).toUpperCase() === st)) {
              filteredCodes.push(st);
            }
          });
          setPorterStatusOptions(filteredCodes);
        }
      } catch (err) {
        console.error('Failed to fetch porter statuses:', err);
      }
    };
    fetchPorterStatuses();
  }, []);

  // All statuses combined for the filter dropdown (used by Online Orders tab)
  const allStatusOptions = React.useMemo(() => {
    const seen = new Set();
    const result = [];
    const terminalList = ['delivered', 'completed', 'cancelled'];
    [...statusOptions, ...porterStatusOptions].forEach(s => {
      const normalized = String(s).toUpperCase().trim();
      if ((!terminalList.includes(String(s).toLowerCase()) || String(s).toLowerCase() === 'rejected') && !seen.has(normalized)) {
        seen.add(normalized);
        result.push(s);
      }
    });
    return result;
  }, [statusOptions, porterStatusOptions]);

  const counterStatusOptions = React.useMemo(() => {
    return ['pending', 'assigned', 'preparing', 'ready', 'out_for_delivery', 'delivered', 'completed', 'cancelled'];
  }, []);

  const campusLifeStatusOptions = React.useMemo(() => {
    return [
      'pending', 'confirmed', 'preparing', 'assigned', 'ready',
      'picked_up', 'travelling', 'out_for_delivery', 'delivered', 'completed', 'cancelled', 'rejected'
    ];
  }, []);

  // Available payment sources (web, android, whatsapp_order, etc.) extracted dynamically from response payment_source
  const availableSources = React.useMemo(() => {
    const defaultSources = ['web', 'android', 'whatsapp_order'];
    const seen = new Set(defaultSources);
    (orders || []).forEach(o => {
      const src = o?.payment_source || o?.payment_details?.payment_source;
      if (src && typeof src === 'string') {
        const val = src.trim().toLowerCase();
        if (val) seen.add(val);
      }
    });
    return Array.from(seen);
  }, [orders]);




  const PAGE_SIZE = 10;
  const currentPage = filters.page || 1;
  const startIndex = (currentPage - 1) * PAGE_SIZE;
  const displayedOrders = React.useMemo(() => {
    return (orders || []).slice(startIndex, startIndex + PAGE_SIZE);
  }, [orders, startIndex]);

  if (loading) {

    return (

      <div className="order-management-loading">

        <div className="loading-spinner"></div>

        <p>Loading orders...</p>

      </div>

    );

  }



  if (error) {

    return (

      <div className="order-management-error">

        <h3>Error Loading Orders</h3>

        <p>{error}</p>

        <button

          onClick={() => {

            setError(null);

            fetchOrders();

          }}

          style={{

            background: '#F55D00',

            color: 'white',

            border: 'none',

            padding: '6px 12px',

            borderRadius: '6px',

            cursor: 'pointer',

            fontSize: '12px',

            fontWeight: '500'

          }}

        >

          Retry

        </button>

      </div>

    );

  }

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    localStorage.setItem('orderManagement_activeTab', tab);
    ordersFingerprint.current = '_tab_changed_';
    setOrders([]);
    previousOrderCount.current = 0;
    setKpiMetrics(null);
    setSearchInput('');
    setFilters(prev => ({
      ...prev,
      status: '',
      order_type: '',
      search: '',
      source: '',
      payment_status: '',
      page: 1
    }));
  };

  const getSortedPorterStatusOptions = () => {
    let list = [...porterStatusOptions];
    ['DELIVERED', 'COMPLETED', 'CANCELLED'].forEach(st => {
      if (!list.some(s => String(s).toUpperCase() === st)) {
        list.push(st);
      }
    });

    if (!selectedOrder) return list;

    let options = list.filter(s => {
      const upper = String(s).toUpperCase();
      if (upper === 'FAILED') return false;
      if (upper === 'PENDING') {
        return String(selectedOrder.payment_status || 'PENDING').toLowerCase() === 'pending';
      }
      return true;
    });

    const predefinedOrder = [
      'SEARCHING_RIDER',
      'PENDING',
      'RIDER_ALLOCATED',
      'RIDER_ACCEPTED',
      'ON_THE_WAY_TO_PICKUP',
      'AT_PICKUP',
      'PICKED_UP',
      'IN_TRANSIT',
      'AT_DELIVERY',
      'DELIVERED',
      'COMPLETED',
      'CANCELLED_BY_CUSTOMER',
      'CANCELLED_BY_RIDER',
      'CANCELLED'
    ];

    return options.sort((a, b) => {
      const aIndex = predefinedOrder.indexOf(String(a).toUpperCase());
      const bIndex = predefinedOrder.indexOf(String(b).toUpperCase());

      if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
      if (aIndex !== -1) return -1;
      if (bIndex !== -1) return 1;
      return 0;
    });
  };

  const getModalStatusOptions = (order) => {
    if (!order) return statusOptions;

    if (order.order_system === 'campuslife' || (order.order_id && String(order.order_id).startsWith('CL_')) || activeTab === 'campuslife') {
      return campusLifeStatusOptions;
    }

    const orderType = (order.order_type || '').toLowerCase().trim().replace(/[-_]/g, ' ');
    const isSelfCollectOrDineIn =
      orderType === 'dine in' ||
      orderType === 'takeaway' ||
      orderType === 'take away' ||
      orderType === 'pickup' ||
      orderType === 'pick up';

    let options = orderType.includes('porter')
      ? getSortedPorterStatusOptions()
      : statusOptions;

    options = options.filter(status => !(status === 'preparing' && order.business_type !== 'R02'));

    if (isSelfCollectOrDineIn) {
      const excludedForSelfCollectOrDineIn = [
        'assigned',
        'picked_up',
        'travelling',
        'out_for_delivery',
        'delivered'
      ];
      options = options.filter(status => !excludedForSelfCollectOrDineIn.includes(status.toLowerCase()));
    }

    return options;
  };

  return (

    <div className={`order-management ${isMobile ? 'mobile' : isDesktop ? 'desktop' : 'tablet'}`}>

      {/* Hidden Audio Element */}
      <audio
        ref={(el) => {
          audioRef.current = el;
        }}
        preload="auto"
      >
        <source src={newOrderSound} type="audio/mpeg" />
      </audio>

      {/* Header */}

      <div className={`order-management-header ${isMobile ? 'mobile' : isDesktop ? 'desktop' : 'tablet'}`}>

        <div className="header-content">

          <div className="header-text">

            <h2>Order Management</h2>

          </div>

          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <button className="header-refresh-btn" onClick={() => fetchOrders(false)} disabled={loading}
              style={{ opacity: loading ? 0.7 : 1, cursor: loading ? 'not-allowed' : 'pointer' }}>
              <MdRefresh className="refresh-icon" style={loading ? { animation: 'spin 0.8s linear infinite' } : undefined} />
              {loading ? 'Refreshing...' : 'Refresh'}
            </button>
            <button
              onClick={handleExportExcel}
              disabled={exportLoading || !orders || orders.length === 0}
              style={{
                display: 'flex', alignItems: 'center', gap: '6px',
                padding: '8px 14px',
                background: exportLoading || !orders || orders.length === 0 ? '#9ca3af' : '#1d6f42',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '13px',
                fontWeight: '600',
                cursor: exportLoading || !orders || orders.length === 0 ? 'not-allowed' : 'pointer',
                transition: 'background 0.2s ease'
              }}
              onMouseEnter={e => { if (!exportLoading && orders?.length > 0) e.currentTarget.style.background = '#155534'; }}
              onMouseLeave={e => { if (!exportLoading && orders?.length > 0) e.currentTarget.style.background = '#1d6f42'; }}
            >
              {exportLoading
                ? <><MdRefresh style={{ fontSize: '14px', animation: 'spin 0.8s linear infinite' }} /> Exporting...</>
                : <><FaFileExcel style={{ fontSize: '14px' }} /> Export</>
              }
            </button>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '12px', color: '#666', fontWeight: '500' }}>Sound</span>
              <label className="toggle-switch" style={{ position: 'relative', display: 'inline-block', width: '44px', height: '24px' }}>
                <input
                  type="checkbox"
                  checked={audioEnabled}
                  onChange={() => {
                    const newState = !audioEnabled;
                    setAudioEnabled(newState);
                    if (newState) {
                      playAudioTwice();
                    }
                  }}
                  style={{ opacity: 0, width: 0, height: 0 }}
                />
                <span
                  style={{
                    position: 'absolute',
                    cursor: 'pointer',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: audioEnabled ? 'rgba(82, 204, 110, 1)' : '#ccc',
                    transition: '.4s',
                    borderRadius: '24px'
                  }}
                ></span>
                <span
                  style={{
                    position: 'absolute',
                    content: '',
                    height: '18px',
                    width: '18px',
                    left: audioEnabled ? '26px' : '3px',
                    bottom: '3px',
                    backgroundColor: 'white',
                    transition: '.4s',
                    borderRadius: '50%'
                  }}
                ></span>
              </label>
              {/* <button
                type="button"
                onClick={() => playAudioTwice()}
                title="Test notification sound"
                style={{
                  padding: '4px 8px',
                  fontSize: '11px',
                  background: '#f1f5f9',
                  border: '1px solid #cbd5e1',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  color: '#334155',
                  fontWeight: '600'
                }}
              >
                Test Sound
              </button> */}
            </div>
          </div>

        </div>

      </div>

      {/* Tabs */}
      <div className="order-tabs-container">
        <button
          onClick={() => handleTabChange('online')}
          className={`order-tab-btn ${activeTab === 'online' ? 'active' : ''}`}
        >
          Online Orders
        </button>
        <button
          onClick={() => handleTabChange('counter')}
          className={`order-tab-btn ${activeTab === 'counter' ? 'active' : ''}`}
        >
          Counter Orders
        </button>
        <button
          onClick={() => handleTabChange('scheduled')}
          className={`order-tab-btn ${activeTab === 'scheduled' ? 'active' : ''}`}
        >
          Scheduled Orders
        </button>
        <button
          onClick={() => handleTabChange('campuslife')}
          className={`order-tab-btn ${activeTab === 'campuslife' ? 'active' : ''}`}
        >
          Campuslife Orders
        </button>
      </div>

      {/* KPI Metrics */}
      {kpiMetrics && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '20px',
          marginBottom: '24px',
          marginTop: '10px'
        }}>
          {Object.entries(kpiMetrics).map(([key, val]) => {
            // Skip objects (like status_counts) or arrays
            if (typeof val === 'object' || Array.isArray(val) || val === null || val === undefined) return null;

            // Format key to a human-readable title (e.g. "total_revenue" -> "Total Revenue")
            const displayTitle = key
              .replace(/_/g, ' ')
              .replace(/\b\w/g, c => c.toUpperCase());

            // Format value (e.g. prefix with Currency symbol if it represents an amount)
            const isAmount = key.includes('revenue') || key.includes('value') || key.includes('discount') || key.includes('tax') || key.includes('amount') || key.includes('gst');
            const displayVal = isAmount
              ? `₹${Number(val || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
              : val;

            // Pick icon and color dynamically based on key
            let icon = <FaClipboardList />;
            let iconBg = '#eff6ff';
            let iconColor = '#3b82f6';

            if (key.includes('revenue')) {
              icon = <FaRupeeSign />;
              iconBg = '#ecfdf5';
              iconColor = '#10b981';
            } else if (key.includes('value')) {
              icon = <FaInfoCircle />;
              iconBg = '#fdf4ff';
              iconColor = '#d946ef';
            } else if (key.includes('discount')) {
              icon = <FaExclamationTriangle style={{ transform: 'rotate(180deg)' }} />;
              iconBg = '#fef2f2';
              iconColor = '#ef4444';
            } else if (key.includes('tax') || key.includes('gst')) {
              icon = <FaBuilding />;
              iconBg = '#fffbeb';
              iconColor = '#f59e0b';
            }

            return (
              <div
                key={key}
                style={{
                  background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                  border: '1px solid #e2e8f0',
                  borderRadius: '16px',
                  padding: '20px',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '16px'
                }}
              >
                <div style={{
                  background: iconBg,
                  color: iconColor,
                  width: '48px',
                  height: '48px',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '20px'
                }}>
                  {icon}
                </div>
                <div>
                  <div style={{ fontSize: '13px', color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    {displayTitle}
                  </div>
                  <div style={{ fontSize: '24px', fontWeight: 800, color: '#1e293b', marginTop: '4px' }}>
                    {displayVal}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Filters */}

      {/* Filters Container - Single Row with Floating Dropdowns */}
      <div className={`order-filters ${isMobile ? 'mobile' : isDesktop ? 'desktop' : 'tablet'}`} style={{ display: 'flex', flexDirection: 'row', flexWrap: 'nowrap', alignItems: 'flex-start', gap: '10px', padding: '16px 20px', overflow: 'visible', position: 'relative', zIndex: 20 }}>

        {/* Search Input Field */}
        <div className={`filter-group ${isMobile ? 'mobile' : isDesktop ? 'desktop' : 'tablet'}`} style={{ minWidth: '200px', flex: '1 1 240px' }}>
          <label>Search Orders</label>
          <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center', flex: 1 }}>
              <FaSearch style={{ position: 'absolute', left: '10px', color: '#94a3b8', fontSize: '12px', pointerEvents: 'none' }} />
              <input
                type="text"
                placeholder="Search Orders..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleFilterChange('search', searchInput);
                  }
                }}
                style={{
                  width: '100%',
                  padding: '6px 24px 6px 28px',
                  border: '1px solid #cbd5e1',
                  borderRadius: '6px',
                  fontSize: '12px',
                  color: '#1e293b',
                  background: '#ffffff',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
              />
              {searchInput && (
                <FaTimes
                  onClick={() => {
                    setSearchInput('');
                    handleFilterChange('search', '');
                  }}
                  style={{ position: 'absolute', right: '8px', color: '#94a3b8', cursor: 'pointer', fontSize: '11px' }}
                  title="Clear Search"
                />
              )}
            </div>
            <button
              type="button"
              onClick={() => handleFilterChange('search', searchInput)}
              style={{
                padding: '6px 12px',
                background: '#F55D00',
                color: '#ffffff',
                border: 'none',
                borderRadius: '6px',
                fontSize: '12px',
                fontWeight: '600',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                transition: 'background 0.2s ease',
                height: '31px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              Apply
            </button>
          </div>
        </div>

        {/* Order Status */}
        {activeTab !== 'scheduled' && (
          <div className={`filter-group ${isMobile ? 'mobile' : isDesktop ? 'desktop' : 'tablet'}`} style={{ minWidth: '120px', flex: '1 1 120px' }}>
            <label>Order Status</label>
            <div className="status-dropdown custom-dropdown">
              <div
                className="dropdown-input"
                onClick={() => setStatusDropdownOpen(!statusDropdownOpen)}
              >
                <span className="dropdown-placeholder">
                  {filters.status ? (
                    (activeTab === 'counter' ? counterStatusOptions : (activeTab === 'campuslife' ? campusLifeStatusOptions : allStatusOptions))
                      .find(s => s.toLowerCase() === filters.status.toLowerCase())?.replace(/_/g, ' ').toUpperCase() || filters.status.toUpperCase()
                  ) : 'All Statuses'}
                </span>
                <FaChevronDown className={`dropdown-arrow ${statusDropdownOpen ? 'open' : ''}`} />
              </div>

              {statusDropdownOpen && (
                <div className="dropdown-menu">
                  <div
                    className="dropdown-option"
                    onClick={() => {
                      handleFilterChange('status', '');
                      setStatusDropdownOpen(false);
                    }}
                  >
                    All Statuses
                  </div>
                  {(activeTab === 'counter' ? counterStatusOptions : (activeTab === 'campuslife' ? campusLifeStatusOptions : allStatusOptions)).map(status => (
                    <div
                      key={status}
                      className="dropdown-option"
                      onClick={() => {
                        handleFilterChange('status', status);
                        setStatusDropdownOpen(false);
                      }}
                    >
                      {status.replace(/_/g, ' ').toUpperCase()}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Order Type */}
        <div className="filter-group" style={{ minWidth: '110px', flex: '1 1 110px' }}>
          <label>Order Type</label>
          <div className="order-type-dropdown custom-dropdown">
            <div
              className="dropdown-input"
              onClick={() => setOrderTypeDropdownOpen(!orderTypeDropdownOpen)}
            >
              <span className="dropdown-placeholder">
                {filters.order_type === 'delivery' ? 'Delivery' :
                  filters.order_type === 'pickup' ? 'Pickup' :
                    filters.order_type === 'dine-in' ? 'Dine In' :
                      filters.order_type === 'takeaway' ? 'Takeaway' :
                        filters.order_type === 'porter delivery' ? 'Porter Delivery' :
                          'All Types'}
              </span>
              <FaChevronDown className={`dropdown-arrow ${orderTypeDropdownOpen ? 'open' : ''}`} />
            </div>

            {orderTypeDropdownOpen && (
              <div className="dropdown-menu">
                <div
                  className="dropdown-option"
                  onClick={() => {
                    handleFilterChange('order_type', '');
                    setOrderTypeDropdownOpen(false);
                  }}
                >
                  All Types
                </div>
                <div
                  className="dropdown-option"
                  onClick={() => {
                    handleFilterChange('order_type', 'delivery');
                    setOrderTypeDropdownOpen(false);
                  }}
                >
                  Delivery
                </div>
                <div
                  className="dropdown-option"
                  onClick={() => {
                    handleFilterChange('order_type', 'pickup');
                    setOrderTypeDropdownOpen(false);
                  }}
                >
                  Pickup
                </div>
                <div
                  className="dropdown-option"
                  onClick={() => {
                    handleFilterChange('order_type', 'dine-in');
                    setOrderTypeDropdownOpen(false);
                  }}
                >
                  Dine In
                </div>
                <div
                  className="dropdown-option"
                  onClick={() => {
                    handleFilterChange('order_type', 'takeaway');
                    setOrderTypeDropdownOpen(false);
                  }}
                >
                  Takeaway
                </div>
                {activeTab !== 'counter' && (
                  <div
                    className="dropdown-option"
                    onClick={() => {
                      handleFilterChange('order_type', 'porter delivery');
                      setOrderTypeDropdownOpen(false);
                    }}
                  >
                    Porter Delivery
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Payment Status */}
        {activeTab !== 'scheduled' && (
          <div className="filter-group" style={{ minWidth: '110px', flex: '1 1 110px' }}>
            <label>Payment Status</label>
            <div className="payment-status-dropdown custom-dropdown">
              <div
                className="dropdown-input"
                onClick={() => setPaymentStatusDropdownOpen(!paymentStatusDropdownOpen)}
              >
                <span className="dropdown-placeholder">
                  {filters.payment_status ? (
                    filters.payment_status === 'success' ? 'Success' :
                      filters.payment_status === 'pending' ? 'Pending' :
                        filters.payment_status === 'failed' ? 'Failed' :
                          filters.payment_status === 'cancelled' ? 'Cancelled' :
                            filters.payment_status === 'paid' ? 'Paid' :
                              filters.payment_status === 'completed' ? 'Completed' :
                                filters.payment_status.toUpperCase()
                  ) : 'All Payments'}
                </span>
                <FaChevronDown className={`dropdown-arrow ${paymentStatusDropdownOpen ? 'open' : ''}`} />
              </div>

              {paymentStatusDropdownOpen && (
                <div className="dropdown-menu">
                  <div className="dropdown-option" onClick={() => { handleFilterChange('payment_status', ''); setPaymentStatusDropdownOpen(false); }}>
                    All Payments
                  </div>
                  {(activeTab === 'counter' ? ['paid', 'pending', 'completed'] : (activeTab === 'campuslife' ? ['success', 'pending', 'failed', 'cancelled'] : ['success', 'pending', 'cod', 'failed', 'cancelled'])).map(ps => (
                    <div
                      key={ps}
                      className="dropdown-option"
                      onClick={() => { handleFilterChange('payment_status', ps); setPaymentStatusDropdownOpen(false); }}
                    >
                      {ps.toUpperCase()}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Source */}
        {activeTab !== 'counter' && (
          <div className="filter-group" style={{ minWidth: '110px', flex: '1 1 110px' }}>
            <label>Source</label>
            <div className="source-dropdown custom-dropdown">
              <div
                className="dropdown-input"
                onClick={() => setSourceDropdownOpen(!sourceDropdownOpen)}
              >
                <span className="dropdown-placeholder">
                  {filters.source ? filters.source.replace(/_/g, ' ').toUpperCase() : 'All Sources'}
                </span>
                <FaChevronDown className={`dropdown-arrow ${sourceDropdownOpen ? 'open' : ''}`} />
              </div>

              {sourceDropdownOpen && (
                <div className="dropdown-menu">
                  <div
                    className="dropdown-option"
                    onClick={() => {
                      handleFilterChange('source', '');
                      setSourceDropdownOpen(false);
                    }}
                  >
                    All Sources
                  </div>
                  {availableSources.map(src => (
                    <div
                      key={src}
                      className="dropdown-option"
                      onClick={() => {
                        handleFilterChange('source', src);
                        setSourceDropdownOpen(false);
                      }}
                    >
                      {src.replace(/_/g, ' ').toUpperCase()}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Date Range Filter */}
        <div className="filter-group" style={{ minWidth: '240px', flex: '0 0 auto' }}>
          <label>Date Range</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <input
              type="date"
              value={dateFromInput || ''}
              onChange={(e) => handleDateChange('date_from', e.target.value)}
              onKeyDown={(e) => handleDateKeyDown(e, 'date_from')}
              style={{
                padding: '6px 6px',
                border: '1px solid #cbd5e1',
                borderRadius: '6px',
                fontSize: '12px',
                color: '#1e293b',
                background: '#ffffff',
                outline: 'none'
              }}
            />
            <span style={{ fontSize: '12px', color: '#64748b' }}>to</span>
            <input
              type="date"
              value={dateToInput || ''}
              onChange={(e) => handleDateChange('date_to', e.target.value)}
              onKeyDown={(e) => handleDateKeyDown(e, 'date_to')}
              style={{
                padding: '6px 6px',
                border: '1px solid #cbd5e1',
                borderRadius: '6px',
                fontSize: '12px',
                color: '#1e293b',
                background: '#ffffff',
                outline: 'none'
              }}
            />
          </div>
        </div>

      </div>



      {/* Error Display - fixed top banner so it's always visible above modals */}

      {error && (

        <div style={{
          position: 'fixed',
          top: '12px',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 9999999,
          backgroundColor: '#f8d7da',
          color: '#721c24',
          padding: '10px 16px',
          borderRadius: '8px',
          boxShadow: '0 8px 20px rgba(0,0,0,0.12)',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          maxWidth: '92%',
          minWidth: '280px',
          justifyContent: 'space-between',
        }}>
          <div style={{ flex: 1, paddingRight: '8px', wordBreak: 'break-word' }}>{error}</div>
          <button onClick={() => setError(null)} style={{
            background: 'transparent', border: 'none', color: '#721c24', fontSize: '18px', cursor: 'pointer'
          }}>×</button>
        </div>

      )}



      {/* Orders Table */}

      <div className={`orders-table-container ${isMobile ? 'mobile' : isDesktop ? 'desktop' : 'tablet'}`}>

        {orders.length === 0 ? (

          <div style={{

            textAlign: 'center',

            padding: '40px',

            background: 'white',

            borderRadius: '8px',

            border: '1px solid #e0e0e0'

          }}>

            <h3>No Orders Found</h3>

            <p>No orders match the current filters.</p>

          </div>

        ) : (

          <>
            {/* Desktop Table View */}

            <div className="orders-table-wrapper">

              <table className={`orders-table ${isMobile ? 'mobile' : isDesktop ? 'desktop' : 'tablet'}`}>

                <thead>

                  <tr>

                    <th>Order #</th>

                    <th>Customer</th>

                    <th>Business</th>

                    <th>Type</th>

                    <th>Amount</th>

                    <th>Order Status</th>

                    <th>Payment Status</th>

                    <th>Delivery Partner</th>

                    <th>Date</th>

                    <th>Actions</th>

                  </tr>

                </thead>

                <tbody>

                  {displayedOrders.map((order) => (

                    <tr key={`${order.order_system}-${order.order_id}`}>

                      <td className="order-id">

                        <div className="order-number">#{formatOrderId(order.order_id)}</div>

                      </td>

                      <td className="customer-info">

                        <div className="customer-name">{order.customer_name}</div>

                        <div className="customer-phone">{order.customer_phone}</div>

                      </td>

                      <td className="business-name">

                        <div className="business-info">

                          <div className="name">{order.business_name}</div>

                        </div>

                      </td>

                      <td className="order-type">

                        <span className={`type-badge ${order.order_type}`}>

                          {order.order_type}

                        </span>

                      </td>

                      <td className="amount">

                        <div className="amount-info">

                          <div className="total">₹{((order.final_amount ?? order.total_amount) ?? 0).toFixed(2)}</div>

                        </div>

                      </td>

                      <td className="status">

                        <span

                          className={`status-badge ${order.status.toLowerCase()}`}

                        >

                          {getStatusDisplay(order)}

                        </span>

                      </td>

                      <td style={{

                        minWidth: '100px',

                        padding: '12px',

                        textAlign: 'center',

                        verticalAlign: 'middle'

                      }}>

                        <div style={{

                          display: 'inline-block',

                          padding: '4px 8px',

                          borderRadius: '12px',

                          fontSize: '11px',

                          fontWeight: '600',

                          textTransform: 'uppercase',

                          backgroundColor: (order.payment_status || '').toLowerCase() === 'success' || (order.payment_status || '').toLowerCase() === 'paid' ? '#d4edda' :

                            (order.payment_status || '').toLowerCase() === 'cancelled' || (order.payment_status || '').toLowerCase() === 'failed' ? '#f8d7da' :
                              order.payment_status === null || order.payment_status === undefined ? '#f1f5f9' : '#fff3cd',

                          color: (order.payment_status || '').toLowerCase() === 'success' || (order.payment_status || '').toLowerCase() === 'paid' ? '#155724' :

                            (order.payment_status || '').toLowerCase() === 'cancelled' || (order.payment_status || '').toLowerCase() === 'failed' ? '#721c24' :
                              order.payment_status === null || order.payment_status === undefined ? '#64748b' : '#856404'

                        }}>

                          {order.payment_status === null || order.payment_status === undefined ? 'null' : String(order.payment_status).toUpperCase()}

                        </div>

                      </td>

                      <td className="delivery-partner">

                        {order.delivery_partner_name ? (

                          <div className="partner-info">

                            <div className="partner-name">{order.delivery_partner_name}</div>

                          </div>

                        ) : (

                          <div className="no-partner">

                            <span className="no-partner-text">Not Assigned</span>

                          </div>

                        )}

                      </td>

                      <td className="created-date">

                        <div className="date-info">

                          <div className="date">{formatDate(order.created_at)}</div>

                        </div>

                      </td>

                      <td className="actions">

                        <div className="action-buttons">

                          <button

                            type="button"

                            className="details-btn"

                            onClick={(e) => {

                              e.stopPropagation();

                              handleOpenDetailsModal(order);

                            }}

                          >

                            View

                          </button>

                          {activeTab !== 'scheduled' && (
                            <button

                              type="button"

                              className="status-btn"

                              onClick={(e) => {

                                e.stopPropagation();

                                handleOpenStatusModal(order);

                              }}

                            >

                              Update

                            </button>
                          )}

                        </div>

                      </td>

                    </tr>

                  ))}

                </tbody>

              </table>

            </div>

            {/* Mobile Card View */}

            <div className="mobile-orders-container">

              {displayedOrders.map((order) => (

                <div key={`mobile-${order.order_system}-${order.order_id}`} className="mobile-order-card">

                  <div className="mobile-order-header">

                    <div className="mobile-order-id">#{formatOrderId(order.order_id)}</div>

                    <span

                      className={`mobile-status-badge ${order.status.toLowerCase()}`}

                    >

                      {getStatusDisplay(order)}

                    </span>

                  </div>



                  <div className="mobile-order-details">

                    <div className="mobile-detail-item">

                      <div className="mobile-detail-label">

                        <FaStore /> Business:

                      </div>

                      <div className="mobile-detail-value">

                        <div className="mobile-business-name">{order.business_name}</div>

                      </div>

                    </div>



                    <div className="mobile-detail-item">

                      <div className="mobile-detail-label">

                        <FaUser /> Name:

                      </div>

                      <div className="mobile-detail-value">

                        <div className="mobile-customer-name">{order.customer_name}</div>

                      </div>

                    </div>



                    <div className="mobile-detail-item">

                      <div className="mobile-detail-label">

                        <FaUser /> Phone:

                      </div>

                      <div className="mobile-detail-value">

                        <div className="mobile-customer-phone">{order.customer_phone}</div>

                      </div>

                    </div>



                    <div className="mobile-detail-item">

                      <div className="mobile-detail-label">

                        <FaClipboardList /> Type:

                      </div>

                      <div className="mobile-detail-value">

                        <span className={`mobile-type-badge ${order.order_type}`}>

                          {order.order_type}

                        </span>

                      </div>

                    </div>



                    <div className="mobile-detail-item">

                      <div className="mobile-detail-label">

                        <FaRupeeSign /> Amount:

                      </div>

                      <div className="mobile-detail-value mobile-amount">

                        {formatCurrency(order.final_amount ?? order.total_amount)}

                      </div>

                    </div>



                    <div className="mobile-detail-item">

                      <div className="mobile-detail-label">

                        <FaCreditCard /> Payment:

                      </div>

                      <div className="mobile-detail-value">

                        <span className={`mobile-payment-badge ${order.payment_status === null || order.payment_status === undefined ? 'null' : String(order.payment_status).toLowerCase()}`} style={order.payment_status === null || order.payment_status === undefined ? { background: '#f1f5f9', color: '#64748b' } : {}}>

                          {order.payment_status === null || order.payment_status === undefined ? 'null' : String(order.payment_status).toUpperCase()}

                        </span>

                      </div>

                    </div>



                    <div className="mobile-detail-item">

                      <div className="mobile-detail-label">

                        <FaMotorcycle /> Partner:

                      </div>

                      <div className="mobile-detail-value">

                        {order.delivery_partner_name ? (

                          <div className="mobile-partner-info">

                            <div className="mobile-partner-name">{order.delivery_partner_name}</div>

                          </div>

                        ) : (

                          <span className="mobile-no-partner">Not Assigned</span>

                        )}

                      </div>

                    </div>



                    <div className="mobile-detail-item">

                      <div className="mobile-detail-label">

                        <FaCalendar /> Date:

                      </div>

                      <div className="mobile-detail-value mobile-created-date">

                        {formatDate(order.created_at)}

                      </div>

                    </div>

                  </div>



                  <div className="mobile-order-actions">

                    <button

                      type="button"

                      className="details-btn"

                      onClick={(e) => {

                        e.stopPropagation();

                        handleOpenDetailsModal(order);

                      }}

                    >

                      <FaEye /> View

                    </button>

                    {activeTab !== 'scheduled' && (
                      <button

                        type="button"

                        className="status-btn"

                        onClick={(e) => {

                          e.stopPropagation();

                          handleOpenStatusModal(order);

                        }}

                      >

                        <FaEdit /> Update

                      </button>
                    )}

                  </div>

                </div>

              ))}

            </div>

          </>

        )}



        {/* Pagination Inside Table Container */}
        {pagination.total_pages > 1 && (() => {
          const current = pagination.current_page || 1;
          const total = pagination.total_pages || 1;

          // Build page list with ellipsis
          const pages = Array.from({ length: total }, (_, i) => i + 1)
            .filter(p => p === 1 || p === total || Math.abs(p - current) <= 1)
            .reduce((acc, p, idx, arr) => {
              if (idx > 0 && p - arr[idx - 1] > 1) acc.push('...');
              acc.push(p);
              return acc;
            }, []);

          const btnBase = {
            width: '44px', height: '44px',
            borderRadius: '10px',
            border: '1px solid #e5e7eb',
            background: 'white',
            cursor: 'pointer',
            fontSize: '15px',
            fontWeight: '600',
            color: '#374151',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            transition: 'all 0.15s ease',
            boxShadow: '0 1px 3px rgba(0,0,0,0.06)'
          };

          return (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '6px',
              padding: '20px 0 8px'
            }}>
              {/* Prev */}
              <button
                onClick={() => handlePageChange(current - 1)}
                disabled={current <= 1}
                style={{
                  ...btnBase,
                  cursor: current <= 1 ? 'not-allowed' : 'pointer',
                  color: current <= 1 ? '#d1d5db' : '#374151'
                }}
              >
                <FaChevronLeft size={13} />
              </button>

              {/* Page numbers */}
              {pages.map((item, idx) =>
                item === '...' ? (
                  <span key={`ellipsis-${idx}`} style={{
                    width: '44px', height: '44px',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '15px', color: '#9ca3af', letterSpacing: '2px'
                  }}>
                    ...
                  </span>
                ) : (
                  <button
                    key={item}
                    onClick={() => handlePageChange(item)}
                    style={{
                      ...btnBase,
                      background: current === item ? '#F55D00' : 'white',
                      color: current === item ? 'white' : '#374151',
                      border: current === item ? '1px solid #F55D00' : '1px solid #e5e7eb',
                      boxShadow: current === item
                        ? '0 4px 12px rgba(245, 93, 0, 0.3)'
                        : '0 1px 3px rgba(0,0,0,0.06)'
                    }}
                  >
                    {item}
                  </button>
                )
              )}

              {/* Next */}
              <button
                onClick={() => handlePageChange(current + 1)}
                disabled={current >= total}
                style={{
                  ...btnBase,
                  cursor: current >= total ? 'not-allowed' : 'pointer',
                  color: current >= total ? '#d1d5db' : '#374151'
                }}
              >
                <FaChevronRight size={13} />
              </button>
            </div>
          );
        })()}

      </div>



      {/* Enhanced Status Update Modal */}

      {showStatusModal && selectedOrder && (

        <div

          className="modal-overlay"

          style={{

            display: 'flex !important',

            position: 'fixed',

            top: 0,

            left: 0,

            right: 0,

            bottom: 0,

            zIndex: 999999,

            backgroundColor: 'rgba(0, 0, 0, 0.5)',

            alignItems: 'center',

            justifyContent: 'center',

            visibility: 'visible',

            opacity: 1

          }}

        >

          <div

            className="enhanced-status-modal"

            onClick={(e) => e.stopPropagation()}

            style={{

              position: 'relative',

              zIndex: 1000000,

              backgroundColor: 'white',

              borderRadius: '16px',

              maxWidth: '650px',

              width: '90%',

              maxHeight: '90vh',

              visibility: 'visible',

              opacity: 1,

              display: 'block'

            }}

          >


            <div className="modal-header">

              <h3>Update Status - #{formatOrderId(selectedOrder?.order_id || selectedOrder?.id) || 'Unknown'}</h3>

              <button

                className="close-btn"

                onClick={(e) => {

                  e.stopPropagation();

                  setShowStatusModal(false);

                }}

              >

                ×

              </button>

            </div>



            <div className="modal-content">

              {error && (

                <div className="error-message" style={{ marginBottom: '20px', padding: '10px', backgroundColor: '#f8d7da', color: '#721c24', borderRadius: '5px' }}>

                  {error}

                  <button onClick={() => setError(null)} style={{ float: 'right', background: 'none', border: 'none', fontSize: '16px' }}>×</button>

                </div>

              )}



              {/* Order Summary */}

              <div className="order-summary-details">

                <div className="order-details-grid">

                  <div className="order-detail-item">

                    <div className="detail-label">Customer : </div>

                    <div className="detail-value customer-name">{selectedOrder.customer_name}</div>

                  </div>

                  <div className="order-detail-item">

                    <div className="detail-label">Amount : </div>

                    <div className="detail-value amount-value">₹{selectedOrder.final_amount ?? selectedOrder.total_amount}</div>

                  </div>

                  <div className="order-detail-item">

                    <div className="detail-label">Order ID : </div>

                    <div className="detail-value">#{formatOrderId(selectedOrder.order_id)}</div>

                  </div>

                  <div className="order-detail-item">

                    <div className="detail-label">Business : </div>

                    <div className="detail-value">{selectedOrder.business_name}</div>

                  </div>

                  <div className="order-detail-item">

                    <div className="detail-label">Order Type : </div>

                    <div className="detail-value">{selectedOrder.order_type}</div>

                  </div>

                  <div className="order-detail-item">

                    <div className="detail-label">Current Status : </div>

                    <div className="detail-value">

                      <span className="current-status-mini-badge">{getStatusDisplay(selectedOrder)}</span>

                    </div>

                  </div>

                </div>

              </div>



              {/* Modal Tabs: Order Status / Payment Status */}

              <div className="status-modal-tabs" style={{ display: 'flex', gap: '8px', marginTop: '16px', marginBottom: '12px', flexWrap: 'wrap' }}>

                <button

                  type="button"

                  className={`tab-btn ${statusModalTab === 'status' ? 'active' : ''}`}

                  onClick={() => { setStatusModalTab('status'); setError(null); setSelectedPaymentStatusToSet(''); }}

                >

                  Order Status

                </button>

                <button

                  type="button"

                  className={`tab-btn ${statusModalTab === 'payment' ? 'active' : ''}`}

                  onClick={() => { setStatusModalTab('payment'); setError(null); setSelectedStatus(''); setSelectedDeliveryPartner(null); setSelectedPaymentStatusToSet(''); }}

                >

                  Payment Status

                </button>

              </div>


              {statusModalTab === 'status' ? (

                <div className="order-status-section">

                  <h3>Order Status</h3>

                  <div className="current-status-display">

                    <span className="current-status-badge">{getStatusDisplay(selectedOrder)}</span>

                  </div>

                  <div className="status-options-grid">

                    {getModalStatusOptions(selectedOrder).map(status => (

                      <button

                        key={status}

                        className={`status-option ${selectedStatus === status ? 'selected' : ''} ${selectedOrder.status?.toLowerCase() === status.toLowerCase() ? 'current' : ''}`}

                        onClick={() => handleStatusSelection(status)}

                        disabled={selectedOrder.status?.toLowerCase() === status.toLowerCase()}

                      >

                        {status.replace(/_/g, ' ')}

                      </button>

                    ))}

                  </div>

                </div>

              ) : (

                <div className="payment-status-section">

                  <h3>Payment Status</h3>

                  <div className="current-payment-display">

                    <span className="current-payment-badge" style={selectedOrder.payment_status === null || selectedOrder.payment_status === undefined ? { background: '#f1f5f9', color: '#64748b' } : {}}>
                      {selectedOrder.payment_status === null || selectedOrder.payment_status === undefined ? 'null' : String(selectedOrder.payment_status).toUpperCase()}
                    </span>

                  </div>

                  <div className="payment-options-grid">

                    {((activeTab === 'counter' || selectedOrder?.order_system === 'counter')
                      ? ['pending', 'paid', 'completed', 'cancelled']
                      : ['pending', 'success', 'cod', 'failed', 'cancelled']
                    ).map(ps => (
                      <button
                        type="button"
                        key={ps}
                        className={`payment-option ${selectedPaymentStatusToSet === ps ? 'selected' : ''} ${(selectedOrder.payment_status || '').toLowerCase() === ps ? 'current' : ''}`}
                        onClick={() => setSelectedPaymentStatusToSet(ps)}
                        disabled={(selectedOrder.payment_status || '').toLowerCase() === ps}
                      >
                        {ps.toUpperCase()}
                      </button>
                    ))}

                  </div>

                </div>

              )}





              {/* Available Delivery Partners Section */}

              {(selectedStatus === 'assigned' || String(selectedStatus).toUpperCase() === 'RIDER_ACCEPTED') && (

                <div className="delivery-partners-section">

                  <h3>Available Delivery Partners</h3>

                  {loadingPartners ? (

                    <div className="loading-state">

                      <div className="loading-spinner"></div>

                      <p>Loading delivery partners...</p>

                    </div>

                  ) : deliveryPartners.length === 0 ? (

                    <div className="no-partners">

                      <p>No available delivery partners found</p>

                    </div>

                  ) : (

                    <>

                      <div className="order-assignment-partners-list">

                        {deliveryPartners.map((partner) => (

                          <div

                            key={partner.provider_id}

                            className={`order-assignment-partner-card ${selectedDeliveryPartner?.provider_id === partner.provider_id ? 'selected' : ''}`}

                            onClick={() => setSelectedDeliveryPartner(partner)}

                          >

                            <div className="partner-info" style={{ display: 'flex', flexDirection: 'column', gap: '6px', width: '100%' }}>

                              {/* Row 1: Name (Left) and Phone (Right) */}
                              <div className="partner-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', margin: 0 }}>

                                <div className="partner-name" style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '6px' }}>

                                  <span className="partner-label" style={{ margin: 0, minWidth: 'auto' }}>Name:</span>

                                  <span className="partner-value">{partner.name}</span>

                                </div>

                                <div className="partner-phone" style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '6px' }}>

                                  <span className="partner-label" style={{ margin: 0, minWidth: 'auto' }}>Phone:</span>

                                  <span className="partner-value">{partner.phone}</span>

                                </div>

                              </div>

                              {/* Row 2: Vehicle (Left) and Status (Right) */}
                              <div className="partner-vehicle" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', margin: 0 }}>

                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>

                                  <span className="partner-label" style={{ margin: 0, minWidth: 'auto' }}>Vehicle:</span>

                                  <span className="partner-value" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>

                                    <FaMotorcycle className="vehicle-icon" />

                                    {partner.vehicle_type}

                                  </span>

                                </div>

                                <span className={`status-pill ${partner.status?.toLowerCase()}`} style={{
                                  padding: '4px 10px',
                                  borderRadius: '20px',
                                  fontSize: '12px',
                                  fontWeight: '600',
                                  textTransform: 'uppercase',
                                  background: partner.status?.toLowerCase() === 'available' ? 'rgba(22, 163, 74, 0.1)' : 'rgba(217, 119, 6, 0.1)',
                                  color: partner.status?.toLowerCase() === 'available' ? '#16a34a' : '#d97706',
                                  border: `1px solid ${partner.status?.toLowerCase() === 'available' ? '#16a34a' : '#d97706'}`
                                }}>
                                  {partner.status}
                                </span>

                              </div>

                            </div>

                            {selectedDeliveryPartner?.provider_id === partner.provider_id && (

                              <div className="selected-indicator">✓</div>

                            )}

                          </div>

                        ))}

                      </div>

                    </>

                  )}

                </div>

              )}



              {/* Assignment Warning */}

              {(selectedStatus === 'assigned' || String(selectedStatus).toUpperCase() === 'RIDER_ACCEPTED') && !selectedDeliveryPartner && (

                <div className="assignment-warning">

                  ⚠️ Please select a delivery partner to assign this order

                </div>

              )}



              {/* Action Buttons */}

              <div className="modal-actions">

                <button

                  className="cancel-btn"

                  onClick={() => setShowStatusModal(false)}

                  disabled={updatingStatus}

                >

                  Cancel

                </button>

                <button

                  className="update-btn"

                  onClick={() => { statusModalTab === 'status' ? handleStatusUpdate() : handlePaymentStatusUpdate(); }}

                  disabled={statusModalTab === 'status' ? (updatingStatus || !selectedStatus || ((selectedStatus === 'assigned' || String(selectedStatus).toUpperCase() === 'RIDER_ACCEPTED') && !selectedDeliveryPartner)) : (paymentUpdating || !selectedPaymentStatusToSet)}

                >

                  {statusModalTab === 'status' ? (updatingStatus ? 'Updating...' : (selectedStatus === 'assigned' || String(selectedStatus).toUpperCase() === 'RIDER_ACCEPTED') ? 'Assign Order' : 'Update Status') : (paymentUpdating ? 'Updating...' : 'Update Payment')}

                </button>

              </div>

            </div>

          </div>

        </div>

      )}



      {/* Order Details Modal - Professional Redesign */}

      {showDetailsModal && selectedOrder && (

        <div

          key={`details-modal-${selectedOrder.id || selectedOrder.order_id}`}

          className="modal-overlay order-details-overlay"

          onClick={(e) => {

            // Close only when clicking the overlay background, not the modal content

            // Also guard against immediate closing after opening (within 300ms)

            if (e.target === e.currentTarget) {

              const timeSinceOpen = Date.now() - modalOpenTimeRef.current;

              if (timeSinceOpen > 300) {


                setShowDetailsModal(false);

              } else {

              }

            }

          }}

          style={{

            display: 'flex !important',

            position: 'fixed',

            top: 0,

            left: 0,

            right: 0,

            bottom: 0,

            zIndex: 999999,

            backgroundColor: 'rgba(0, 0, 0, 0.6)',

            alignItems: 'center',

            justifyContent: 'center',

            padding: '20px',

            visibility: 'visible !important',

            opacity: 1

          }}

        >

          {(() => {

            try {
              const orderTypeLower = selectedOrder.order_type?.toLowerCase() || '';
              const isDeliveryType = orderTypeLower.includes('delivery') || orderTypeLower.includes('porter') || Boolean(selectedOrder.delivery_address && selectedOrder.delivery_address !== 'N/A');

              return (

                <>

                  <div

                    className="order-details-modal"

                    onClick={(e) => e.stopPropagation()}

                    style={{

                      backgroundColor: '#f8fafc',

                      borderRadius: '20px',

                      maxWidth: '900px',

                      width: '100%',

                      maxHeight: '90vh',

                      overflow: 'hidden',

                      display: 'flex !important',

                      flexDirection: 'column',

                      boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',

                      visibility: 'visible !important',

                      opacity: 1

                    }}

                  >

                    {/* Modal Header */}

                    <div style={{

                      background: 'linear-gradient(135deg, #F55D00 0%, #FDBF50 100%)',

                      padding: '24px 32px',

                      color: 'white',

                      display: 'flex',

                      justifyContent: 'space-between',

                      alignItems: 'center'

                    }}>

                      <div>

                        <h2 style={{ margin: 0, fontSize: '24px', fontWeight: 700 }}>

                          Order #{selectedOrder.order_id || selectedOrder.id}

                        </h2>

                      </div>

                      <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>

                        <span style={{

                          background: 'rgba(255,255,255,0.2)',

                          padding: '6px 14px',

                          borderRadius: '20px',

                          fontSize: '13px',

                          fontWeight: 600,

                          textTransform: 'uppercase'

                        }}>

                          {selectedOrder.order_type}

                        </span>

                        <button

                          onClick={() => setShowDetailsModal(false)}

                          style={{

                            background: 'rgba(255,255,255,0.2)',

                            border: 'none',

                            color: 'white',

                            width: '36px',

                            height: '36px',

                            borderRadius: '50%',

                            cursor: 'pointer',

                            fontSize: '20px',

                            display: 'flex',

                            alignItems: 'center',

                            justifyContent: 'center'

                          }}

                        >

                          ×

                        </button>

                      </div>

                    </div>



                    {/* Order Details Content - Clean Display Layout */}

                    <div style={{
                      overflowY: 'auto',
                      flex: 1,
                      padding: '24px'
                    }}>

                      {/* Order Information and Customer Details in Single Row */}
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: '20px',
                        marginBottom: '20px'
                      }}>
                        {/* Left Column: Order Information Card & Receiver Details Card (for porter delivery) */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                          {/* Order Information Card */}
                          <div style={{
                            background: 'white',
                            borderRadius: '16px',
                            padding: '24px',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                          }}>
                            <h3 style={{
                              margin: '0 0 20px 0',
                              fontSize: '18px',
                              fontWeight: 700,
                              color: '#1e293b',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px'
                            }}>
                              <FaInfoCircle style={{ fontSize: '20px', color: '#F55D00' }} />
                              Order Information
                            </h3>
                            <div style={{
                              display: 'grid',
                              gridTemplateColumns: '1fr',
                              gap: '8px'
                            }}>
                              <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: '4px 0'
                              }}>
                                <span style={{ fontSize: '14px', color: '#64748b', fontWeight: 500 }}>Order Status:</span>
                                <span className={`status-badge ${selectedOrder.status.toLowerCase()}`} style={{
                                  margin: 0,
                                  fontSize: '12px',
                                  padding: '4px 12px'
                                }}>
                                  {getStatusDisplay(selectedOrder)}
                                </span>
                              </div>
                              <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: '4px 0'
                              }}>
                                <span style={{ fontSize: '14px', color: '#64748b', fontWeight: 500 }}>Payment Status:</span>
                                <span className={`payment-badge ${selectedOrder.payment_status === null || selectedOrder.payment_status === undefined ? 'null' : String(selectedOrder.payment_status).toLowerCase()}`} style={{
                                  margin: 0,
                                  fontSize: '12px',
                                  padding: '4px 12px',
                                  ...(selectedOrder.payment_status === null || selectedOrder.payment_status === undefined ? { background: '#f1f5f9', color: '#64748b' } : {})
                                }}>
                                  {selectedOrder.payment_status === null || selectedOrder.payment_status === undefined ? 'null' : String(selectedOrder.payment_status).toUpperCase()}
                                </span>
                              </div>

                              <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: '4px 0'
                              }}>
                                <span style={{ fontSize: '14px', color: '#64748b', fontWeight: 500 }}>Payment Source:</span>
                                <span style={{ fontSize: '14px', color: '#1e293b', fontWeight: 600 }}>
                                  {String(selectedOrder.payment_source || selectedOrder.payment_details?.payment_source || selectedOrder.source || '—').replace(/_/g, ' ').toUpperCase()}
                                </span>
                              </div>

                              <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: '4px 0'
                              }}>
                                <span style={{ fontSize: '14px', color: '#64748b', fontWeight: 500 }}>Created Date:</span>
                                <span style={{ fontSize: '14px', color: '#1e293b', fontWeight: 600 }}>
                                  {formatDate(selectedOrder.created_at)}
                                </span>
                              </div>

                              {selectedOrder.scheduled_time && (
                                <div style={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  padding: '4px 0',
                                  borderTop: '1px dashed #e2e8f0',
                                  marginTop: '4px',
                                  paddingTop: '8px'
                                }}>
                                  <span style={{ fontSize: '14px', color: '#64748b', fontWeight: 500 }}>Scheduled For:</span>
                                  <span style={{ fontSize: '14px', color: '#F55D00', fontWeight: 700 }}>
                                    {formatDateTime(selectedOrder.scheduled_time)}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Receiver Details Card — only for porter delivery */}
                          {(() => {
                            const isPorter = selectedOrder.order_type?.toLowerCase().includes('porter') ||
                              selectedOrder.order_type?.toLowerCase() === 'porter_delivery' ||
                              selectedOrder.order_type?.toLowerCase() === 'porter delivery' ||
                              Boolean(selectedOrder.porter_details);

                            if (!isPorter) return null;

                            const porterObj = (selectedOrder.porter_details && typeof selectedOrder.porter_details === 'object')
                              ? selectedOrder.porter_details
                              : (typeof selectedOrder.porter_details === 'string'
                                ? (() => { try { return JSON.parse(selectedOrder.porter_details); } catch (e) { return {}; } })()
                                : {});

                            const receiverName = porterObj.receiver_name || selectedOrder.receiver_name;
                            const receiverPhone = porterObj.receiver_phone || selectedOrder.receiver_phone;

                            if (!receiverName && !receiverPhone) return null;

                            return (
                              <div style={{
                                background: 'white',
                                borderRadius: '16px',
                                padding: '24px',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                              }}>
                                <h3 style={{
                                  margin: '0 0 20px 0',
                                  fontSize: '18px',
                                  fontWeight: 700,
                                  color: '#1e293b',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '8px'
                                }}>
                                  <FaUser style={{ fontSize: '20px', color: '#F55D00' }} />
                                  Receiver Details
                                </h3>
                                <div style={{
                                  display: 'grid',
                                  gridTemplateColumns: '1fr',
                                  gap: '8px'
                                }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0' }}>
                                    <span style={{ fontSize: '14px', color: '#64748b', fontWeight: 500 }}>Receiver Name:</span>
                                    <span style={{ fontSize: '14px', color: '#1e293b', fontWeight: 600 }}>
                                      {receiverName || 'N/A'}
                                    </span>
                                  </div>

                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0' }}>
                                    <span style={{ fontSize: '14px', color: '#64748b', fontWeight: 500 }}>Receiver Phone:</span>
                                    <span style={{ fontSize: '14px', color: '#1e293b', fontWeight: 600 }}>
                                      {receiverPhone || 'N/A'}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            );
                          })()}
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                          {/* Customer Details Card */}
                          <div style={{
                            background: 'white',
                            borderRadius: '16px',
                            padding: '24px',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                          }}>
                            <h3 style={{
                              margin: '0 0 20px 0',
                              fontSize: '18px',
                              fontWeight: 700,
                              color: '#1e293b',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px'
                            }}>
                              <FaUser style={{ fontSize: '20px', color: '#F55D00' }} />
                              Customer Details
                            </h3>
                            <div style={{
                              display: 'grid',
                              gridTemplateColumns: ((activeTab === 'online' || activeTab === 'scheduled') && selectedOrder.payment_details) ? '1fr 1fr' : '1fr',
                              gap: '12px 24px'
                            }}>
                              <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: '4px 0'
                              }}>
                                <span style={{ fontSize: '14px', color: '#64748b', fontWeight: 500 }}>Customer Name:</span>
                                <span style={{ fontSize: '14px', color: '#1e293b', fontWeight: 600 }}>
                                  {selectedOrder.customer?.display_name || selectedOrder.customer_name || 'N/A'}
                                </span>
                              </div>
                              <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: '4px 0'
                              }}>
                                <span style={{ fontSize: '14px', color: '#64748b', fontWeight: 500 }}>Phone Number:</span>
                                <span style={{ fontSize: '14px', color: '#1e293b', fontWeight: 600 }}>
                                  {selectedOrder.customer?.phone || selectedOrder.customer_phone || 'N/A'}
                                </span>
                              </div>

                              {isManualOrder(selectedOrder) && (selectedOrder.ordered_by_name || selectedOrder.ordered_by_phone) && (
                                <div style={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  padding: '4px 0'
                                }}>
                                  <span style={{ fontSize: '14px', color: '#64748b', fontWeight: 500 }}>Ordered By:</span>
                                  <span style={{ fontSize: '14px', color: '#1e293b', fontWeight: 600 }}>
                                    {[selectedOrder.ordered_by_name, selectedOrder.ordered_by_phone].filter(Boolean).join(' - ')}
                                  </span>
                                </div>
                              )}

                              {/* Pickup Address — only for porter delivery */}
                              {selectedOrder.order_type?.toLowerCase().includes('porter') && (
                                <div style={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'flex-start',
                                  padding: '4px 0',
                                  gridColumn: '1 / -1'
                                }}>
                                  <span style={{ fontSize: '14px', color: '#64748b', fontWeight: 500, flexShrink: 0 }}>Pickup Address:</span>
                                  <span style={{ fontSize: '14px', color: '#1e293b', fontWeight: 600, textAlign: 'right', maxWidth: '65%', lineHeight: '1.5' }}>
                                    {(typeof selectedOrder.pickup_address === 'string' ? selectedOrder.pickup_address : null) ||
                                      selectedOrder.pickup_address?.full_address ||
                                      selectedOrder.pickup_address?.address ||
                                      'N/A'}
                                  </span>
                                </div>
                              )}

                            </div>
                          </div>

                          {/* Payment Details Card */}
                          {(activeTab === 'online' || activeTab === 'scheduled') && selectedOrder.payment_details && (
                            <div style={{
                              background: 'white',
                              borderRadius: '16px',
                              padding: '24px',
                              boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                            }}>
                              <h3 style={{
                                margin: '0 0 20px 0',
                                fontSize: '18px',
                                fontWeight: 700,
                                color: '#1e293b',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                              }}>
                                <FaCreditCard style={{ fontSize: '20px', color: '#F55D00' }} />
                                Payment Details
                              </h3>
                              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 24px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0' }}>
                                  <span style={{ fontSize: '14px', color: '#64748b', fontWeight: 500 }}>Payment ID:</span>
                                  <span style={{ fontSize: '14px', color: '#1e293b', fontWeight: 600 }}>
                                    {selectedOrder.payment_details.payment_id || 'N/A'}
                                  </span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0' }}>
                                  <span style={{ fontSize: '14px', color: '#64748b', fontWeight: 500 }}>Amount Paid:</span>
                                  <span style={{ fontSize: '14px', color: '#1e293b', fontWeight: 600 }}>
                                    ₹{Number(selectedOrder.payment_details.amount_paid || 0).toFixed(2)}
                                  </span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0' }}>
                                  <span style={{ fontSize: '14px', color: '#64748b', fontWeight: 500 }}>Paid Via:</span>
                                  <span style={{ fontSize: '14px', color: '#1e293b', fontWeight: 600 }}>
                                    {selectedOrder.payment_details.paid_via || 'N/A'}
                                  </span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0' }}>
                                  <span style={{ fontSize: '14px', color: '#64748b', fontWeight: 500 }}>Source:</span>
                                  <span style={{ fontSize: '14px', color: '#1e293b', fontWeight: 600 }}>
                                    {selectedOrder.payment_details.payment_source || 'N/A'}
                                  </span>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>

                      </div>

                      {/* Rejection and Refund Details Section - Only shown when order status is rejected */}
                      {(() => {
                        const isRejectedOrder = selectedOrder.status?.toLowerCase() === 'rejected' ||
                          selectedOrder.order_status?.toLowerCase() === 'rejected';

                        if (!isRejectedOrder) return null;

                        const refundAmount = selectedOrder.refund_amount;
                        const refundStatus = selectedOrder.refund_status;
                        const isRefundCompleted = selectedOrder.is_refund_completed;

                        return (
                          <div style={{
                            display: 'grid',
                            gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                            gap: '20px',
                            marginBottom: '20px'
                          }}>
                            {/* Rejection Details Card */}
                            <div style={{
                              background: 'white',
                              borderRadius: '16px',
                              padding: '24px',
                              boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                            }}>
                              <h3 style={{
                                margin: '0 0 20px 0',
                                fontSize: '18px',
                                fontWeight: 700,
                                color: '#1e293b',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                              }}>
                                <FaBan style={{ fontSize: '20px', color: '#F55D00' }} />
                                Rejection Details
                              </h3>

                              <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '8px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0' }}>
                                  <span style={{ fontSize: '14px', color: '#64748b', fontWeight: 500 }}>Rejected By:</span>
                                  <span style={{ fontSize: '14px', color: '#1e293b', fontWeight: 600 }}>
                                    {selectedOrder.rejected_by || 'N/A'}
                                  </span>
                                </div>

                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0' }}>
                                  <span style={{ fontSize: '14px', color: '#64748b', fontWeight: 500 }}>Rejected Date:</span>
                                  <span style={{ fontSize: '14px', color: '#1e293b', fontWeight: 600 }}>
                                    {selectedOrder.rejected_at ? formatDateTime(selectedOrder.rejected_at) : 'N/A'}
                                  </span>
                                </div>

                                <div style={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  padding: '4px 0'
                                }}>
                                  <span style={{ fontSize: '14px', color: '#64748b', fontWeight: 500 }}>Rejection Reason:</span>
                                  <span style={{ fontSize: '14px', color: '#1e293b', fontWeight: 600 }}>
                                    {selectedOrder.rejection_reason || 'N/A'}
                                  </span>
                                </div>
                              </div>
                            </div>

                            {/* Refund Details Card */}
                            <div style={{
                              background: 'white',
                              borderRadius: '16px',
                              padding: '24px',
                              boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                            }}>
                              <h3 style={{
                                margin: '0 0 20px 0',
                                fontSize: '18px',
                                fontWeight: 700,
                                color: '#1e293b',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                              }}>
                                <FaUndo style={{ fontSize: '20px', color: '#F55D00' }} />
                                Refund Details
                              </h3>

                              <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '8px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0' }}>
                                  <span style={{ fontSize: '14px', color: '#64748b', fontWeight: 500 }}>Refund Status:</span>
                                  <span style={{ fontSize: '14px', color: '#1e293b', fontWeight: 600 }}>
                                    {refundStatus ? String(refundStatus).toUpperCase() : 'N/A'}
                                  </span>
                                </div>

                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0' }}>
                                  <span style={{ fontSize: '14px', color: '#64748b', fontWeight: 500 }}>Refund Amount:</span>
                                  <span style={{ fontSize: '14px', color: '#1e293b', fontWeight: 600 }}>
                                    {refundAmount !== undefined && refundAmount !== null ? `₹${Number(refundAmount).toFixed(2)}` : 'N/A'}
                                  </span>
                                </div>

                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0' }}>
                                  <span style={{ fontSize: '14px', color: '#64748b', fontWeight: 500 }}>Is Refund Completed:</span>
                                  <span style={{ fontSize: '14px', color: '#1e293b', fontWeight: 600 }}>
                                    {isRefundCompleted !== undefined && isRefundCompleted !== null ? (isRefundCompleted ? 'Yes' : 'No') : 'N/A'}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })()}

                      {/* Status Timeline Card */}
                      <div style={{
                        background: 'white',
                        borderRadius: '16px',
                        padding: '16px 20px',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                        marginBottom: '20px'
                      }}>
                        <h3 style={{ margin: '0 0 12px 0', fontSize: '16px', fontWeight: 700, color: '#1e293b' }}>Status Timeline</h3>
                        {Array.isArray(selectedOrder?.status_timeline) && selectedOrder.status_timeline.length > 0 ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            {selectedOrder.status_timeline.map((st, i) => (
                              <div key={i} style={{ display: 'flex', gap: '12px', alignItems: 'flex-start', padding: '8px 0', borderBottom: i < selectedOrder.status_timeline.length - 1 ? '1px solid #f1f5f9' : 'none' }}>
                                <div style={{ width: '12px', display: 'flex', justifyContent: 'center' }}>
                                  <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#F59E0B', marginTop: '6px' }} />
                                </div>
                                <div style={{ flex: 1 }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <div style={{ fontWeight: 700, color: '#0f172a' }}>{st.label || st.status}</div>
                                    <div style={{ fontSize: '12px', color: '#64748b' }}>{st.timestamp ? formatDateTime(st.timestamp) : ''}</div>
                                  </div>
                                  {st.notes ? <div style={{ marginTop: '6px', color: '#334155', fontSize: '13px' }}>{st.notes}</div> : null}
                                  <div style={{ marginTop: '6px', fontSize: '12px', color: '#64748b' }}>By: {st.changed_by || st.source || 'system'}</div>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div style={{ color: '#64748b' }}>No status timeline available for this order.</div>
                        )}
                      </div>

                      {/* Delivery Partner and Address Details (Left Column) - Business Details (Right Column) */}
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: isDeliveryType ? '1fr 1fr' : '1fr',
                        gap: '20px',
                        marginBottom: '20px'
                      }}>
                        {/* Left Column: Delivery Partner Details + Delivery Address */}
                        {isDeliveryType && (
                          <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '20px'
                          }}>
                            {/* Delivery Partner Details */}
                            {((selectedOrder.delivery_partner || selectedOrder.delivery_partner_name) ? (
                              <div style={{
                                background: 'white',
                                borderRadius: '16px',
                                padding: '24px',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                              }}>
                                <h3 style={{
                                  margin: '0 0 20px 0',
                                  fontSize: '18px',
                                  fontWeight: 700,
                                  color: '#1e293b',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '8px'
                                }}>
                                  <FaMotorcycle style={{ fontSize: '20px', color: '#F55D00' }} />
                                  Delivery Partner Details
                                </h3>
                                <div style={{
                                  display: 'grid',
                                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                                  gap: '6px'
                                }}>
                                  {/* Name in first row */}
                                  <div style={{
                                    gridColumn: '1 / -1',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    padding: '6px 0'
                                  }}>
                                    <span style={{ fontSize: '14px', color: '#64748b', fontWeight: 500 }}>Name:</span>
                                    <span style={{ fontSize: '14px', color: '#1e293b', fontWeight: 600 }}>
                                      {selectedOrder.delivery_partner?.name || selectedOrder.delivery_partner_name || 'N/A'}
                                    </span>
                                  </div>

                                  {/* Vehicle Type and Phone in second row */}
                                  <div style={{
                                    gridColumn: '1 / -1',
                                    display: 'grid',
                                    gridTemplateColumns: '1fr 1fr',
                                    gap: '16px',
                                    padding: '4px 0'
                                  }}>
                                    <div style={{
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                      alignItems: 'center'
                                    }}>
                                      <span style={{ fontSize: '14px', color: '#64748b', fontWeight: 500 }}>Vehicle Type:</span>
                                      <span style={{ fontSize: '14px', color: '#1e293b', fontWeight: 600 }}>
                                        {selectedOrder.delivery_partner?.vehicle_type || 'N/A'}
                                      </span>
                                    </div>
                                    <div style={{
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                      alignItems: 'center'
                                    }}>
                                      <span style={{ fontSize: '14px', color: '#64748b', fontWeight: 500 }}>Phone:</span>
                                      <span style={{ fontSize: '14px', color: '#1e293b', fontWeight: 600 }}>
                                        {selectedOrder.delivery_partner?.phone || selectedOrder.delivery_partner_phone || 'N/A'}
                                      </span>
                                    </div>
                                  </div>

                                  {selectedOrder.delivery_partner?.latitude && selectedOrder.delivery_partner?.longitude && (
                                    <div style={{
                                      gridColumn: '1 / -1',
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                      alignItems: 'center',
                                      padding: '4px 0'
                                    }}>
                                      <span style={{ fontSize: '14px', color: '#64748b', fontWeight: 500 }}>Location:</span>
                                      <span style={{ fontSize: '14px', color: '#1e293b', fontWeight: 600 }}>
                                        Available
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            ) : (
                              <div style={{
                                background: '#fef3c7',
                                borderRadius: '16px',
                                padding: '16px 20px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px'
                              }}>
                                <FaExclamationTriangle style={{ fontSize: '18px', color: '#f59e0b' }} />
                                <span style={{ fontSize: '14px', fontWeight: 600, color: '#92400e' }}>
                                  Delivery partner not assigned yet
                                </span>
                              </div>
                            ))}

                            {/* Delivery Address Card */}
                            <div style={{
                              background: 'white',
                              borderRadius: '16px',
                              padding: '24px',
                              boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                            }}>
                              <h3 style={{
                                margin: '0 0 20px 0',
                                fontSize: '18px',
                                fontWeight: 700,
                                color: '#1e293b',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                              }}>
                                <FaMapMarkerAlt style={{ fontSize: '20px', color: '#F55D00' }} />
                                Delivery Address
                              </h3>
                              <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '8px' }}>
                                <div style={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'flex-start',
                                  padding: '4px 0'
                                }}>
                                  <span style={{ fontSize: '14px', color: '#64748b', fontWeight: 500 }}>
                                    {selectedOrder.order_type?.toLowerCase().includes('porter') ? 'Delivery:' : 'Address:'}
                                  </span>
                                  <span style={{ fontSize: '14px', color: '#1e293b', fontWeight: 600, textAlign: 'right', maxWidth: '70%', lineHeight: '1.4' }}>
                                    {(() => {
                                      // 1. Check if delivery_address is already a formatted string
                                      if (typeof selectedOrder.delivery_address === 'string' && selectedOrder.delivery_address.trim() && selectedOrder.delivery_address !== 'N/A') {
                                        return selectedOrder.delivery_address;
                                      }

                                      // 2. Check if delivery_address is an object
                                      if (selectedOrder.delivery_address && typeof selectedOrder.delivery_address === 'object') {
                                        const da = selectedOrder.delivery_address;
                                        if (da.address) return da.address;
                                        if (da.full_address) return da.full_address;
                                        if (da.formatted_address) return da.formatted_address;
                                        const parts = [
                                          da.door_no || da.doorNo || da.flat_no || da.flatNo || da.door,
                                          da.street || da.area || da.locality,
                                          da.landmark ? `Near ${da.landmark}` : null,
                                          da.city,
                                          da.state,
                                          da.pincode || da.pin_code || da.pin
                                        ].filter(Boolean);
                                        if (parts.length > 0) return parts.join(', ');
                                      }

                                      // 3. Check delivery_address_details.formatted_address
                                      if (selectedOrder.delivery_address_details?.formatted_address) {
                                        return selectedOrder.delivery_address_details.formatted_address;
                                      }

                                      // 4. Check delivery_address_details.raw_address
                                      if (selectedOrder.delivery_address_details?.raw_address) {
                                        const raw = selectedOrder.delivery_address_details.raw_address;
                                        if (typeof raw === 'object' && raw !== null) {
                                          if (raw.address) return raw.address;
                                          if (raw.full_address) return raw.full_address;
                                          const parts = [
                                            raw.door_no || raw.doorNo || raw.flat_no || raw.flatNo,
                                            raw.street || raw.area || raw.locality,
                                            raw.landmark ? `Near ${raw.landmark}` : null,
                                            raw.city,
                                            raw.state,
                                            raw.pincode || raw.pin_code
                                          ].filter(Boolean);
                                          if (parts.length > 0) return parts.join(', ');
                                        } else if (typeof raw === 'string') {
                                          try {
                                            const parsed = JSON.parse(raw);
                                            if (parsed && typeof parsed === 'object') {
                                              if (parsed.address) return parsed.address;
                                              if (parsed.full_address) return parsed.full_address;
                                              const parts = [
                                                parsed.door_no || parsed.doorNo || parsed.flat_no || parsed.flatNo,
                                                parsed.street || parsed.area || parsed.locality,
                                                parsed.landmark ? `Near ${parsed.landmark}` : null,
                                                parsed.city,
                                                parsed.state,
                                                parsed.pincode || parsed.pin_code
                                              ].filter(Boolean);
                                              if (parts.length > 0) return parts.join(', ');
                                            }
                                          } catch (_) {
                                            if (raw.trim()) return raw;
                                          }
                                        }
                                      }

                                      // 5. Customer address fallback
                                      if (selectedOrder.customer?.address) {
                                        return typeof selectedOrder.customer.address === 'string'
                                          ? selectedOrder.customer.address
                                          : (selectedOrder.customer.address.address || selectedOrder.customer.address.full_address || 'N/A');
                                      }

                                      return (typeof selectedOrder.delivery_address === 'string' ? selectedOrder.delivery_address : 'N/A');
                                    })()}
                                  </span>
                                </div>

                                {(() => {
                                  let instructions = null;

                                  // 1. From delivery_address_details.instructions
                                  if (selectedOrder.delivery_address_details?.instructions) {
                                    instructions = selectedOrder.delivery_address_details.instructions;
                                  }

                                  // 2. From delivery_address_details.raw_address
                                  if (!instructions && selectedOrder.delivery_address_details?.raw_address) {
                                    const raw = selectedOrder.delivery_address_details.raw_address;
                                    if (typeof raw === 'object' && raw !== null && raw.instructions) {
                                      instructions = raw.instructions;
                                    } else if (typeof raw === 'string') {
                                      try {
                                        const parsed = JSON.parse(raw);
                                        if (parsed && parsed.instructions) {
                                          instructions = parsed.instructions;
                                        }
                                      } catch (_) {}
                                    }
                                  }

                                  // 3. From delivery_address object instructions
                                  if (!instructions && selectedOrder.delivery_address && typeof selectedOrder.delivery_address === 'object' && selectedOrder.delivery_address.instructions) {
                                    instructions = selectedOrder.delivery_address.instructions;
                                  }

                                  // 4. Fallback to delivery_instruction / order_instruction
                                  if (!instructions) {
                                    instructions = selectedOrder.delivery_instruction || selectedOrder.order_instruction;
                                  }

                                  if (!instructions || !String(instructions).trim() || String(instructions).trim().toLowerCase() === 'null') {
                                    return null;
                                  }

                                  return (
                                    <div style={{
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                      alignItems: 'flex-start',
                                      padding: '4px 0',
                                      borderTop: '1px dashed #e2e8f0',
                                      marginTop: '4px',
                                      paddingTop: '6px'
                                    }}>
                                      <span style={{ fontSize: '13px', color: '#64748b', fontWeight: 500 }}>Instructions:</span>
                                      <span style={{ fontSize: '13px', color: '#0f172a', fontWeight: 500, textAlign: 'right', maxWidth: '70%' }}>
                                        {instructions}
                                      </span>
                                    </div>
                                  );
                                })()}
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Right Column: Business Details Card */}
                        <div style={{
                          background: 'white',
                          borderRadius: '16px',
                          padding: '24px',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                        }}>
                          <h3 style={{
                            margin: '0 0 20px 0',
                            fontSize: '18px',
                            fontWeight: 700,
                            color: '#1e293b',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                          }}>
                            <FaBuilding style={{ fontSize: '20px', color: '#F55D00' }} />
                            Business Details
                          </h3>
                          <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                            gap: '20px'
                          }}>
                            <div style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              padding: '4px 0'
                            }}>
                              <span style={{ fontSize: '14px', color: '#64748b', fontWeight: 500 }}>Business Name:</span>
                              <span style={{ fontSize: '14px', color: '#1e293b', fontWeight: 600 }}>
                                {selectedOrder.business_details?.business_name || selectedOrder.business_name || 'N/A'}
                              </span>
                            </div>
                            <div style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              padding: '4px 0'
                            }}>
                              <span style={{ fontSize: '14px', color: '#64748b', fontWeight: 500 }}>Contact Number:</span>
                              <span style={{ fontSize: '14px', color: '#1e293b', fontWeight: 600 }}>
                                {selectedOrder.business_contact?.business_number || selectedOrder.business_details?.business_number || 'N/A'}
                              </span>
                            </div>
                            {(selectedOrder.business_details?.address || selectedOrder.business_details?.business_address || selectedOrder.business?.address) && (
                              <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'flex-start',
                                padding: '4px 0',
                                gridColumn: '1 / -1',
                                gap: '16px'
                              }}>
                                <span style={{ fontSize: '14px', color: '#64748b', fontWeight: 500, flexShrink: 0 }}>Business Address:</span>
                                <span style={{ fontSize: '14px', color: '#1e293b', fontWeight: 600, textAlign: 'right', maxWidth: '70%', lineHeight: '1.4' }}>
                                  {[
                                    selectedOrder.business_details?.address || selectedOrder.business_details?.business_address || selectedOrder.business?.address,
                                    selectedOrder.business_details?.landmark || selectedOrder.business?.landmark,
                                    selectedOrder.business_details?.city || selectedOrder.business?.city,
                                    selectedOrder.business_details?.state || selectedOrder.business?.state
                                  ].filter(Boolean).join(', ')}
                                  {(selectedOrder.business_details?.pincode || selectedOrder.business?.pincode) && ` - ${selectedOrder.business_details?.pincode || selectedOrder.business?.pincode}`}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Order Items and Order Summary in Single Row */}
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: '20px',
                        marginBottom: '20px'
                      }}>
                        {/* Order Items Section */}
                        {selectedOrder.items && selectedOrder.items.length > 0 && (
                          <div style={{
                            background: 'white',
                            borderRadius: '16px',
                            padding: '20px',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                            maxHeight: '400px',
                            overflowY: 'auto'
                          }}>
                            <h3 style={{
                              margin: '0 0 16px 0',
                              fontSize: '16px',
                              fontWeight: 700,
                              color: '#1e293b',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px'
                            }}>
                              <FaBox style={{ fontSize: '18px', color: '#F55D00' }} />
                              Order Items ({selectedOrder.items.length})
                            </h3>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                              {selectedOrder.items.map((item, index) => {
                                const itemName = item.product_name || item.name || item.item_name || 'Item';
                                const rawVariant = item.variant_name || item.size_label || item.variant_size || (typeof item.variant === 'string' ? item.variant : item.variant?.name) || (item.variant_details?.variant_name) || '';
                                const variantStr = typeof rawVariant === 'object' && rawVariant !== null
                                  ? (rawVariant.variant_name || rawVariant.name || rawVariant.size_label || rawVariant.value || '')
                                  : String(rawVariant || '').trim();
                                const qty = Number(item.quantity || item.qty || 1);
                                const unitPrice = Number(item.unit_price || item.price || 0);
                                const totalPrice = Number(item.total_price || item.sub_total || (unitPrice * qty) || 0);

                                return (
                                  <div
                                    key={index}
                                    style={{
                                      display: 'flex',
                                      gap: '12px',
                                      padding: '10px 0',
                                      alignItems: 'center',
                                      borderBottom: index < selectedOrder.items.length - 1 ? '1px dashed #e2e8f0' : 'none'
                                    }}
                                  >
                                    {(() => {
                                      const rawImg = item.image || item.item_image || item.product_image || item.main_image;
                                      const imgSrc = rawImg && typeof rawImg === 'string' && rawImg.trim() && rawImg !== 'null' && rawImg !== 'undefined'
                                        ? (rawImg.startsWith('http://') || rawImg.startsWith('https://')
                                            ? rawImg
                                            : `https://kirazee-bucket.s3.ap-south-1.amazonaws.com/dev/media/${rawImg.replace(/^\/?(media\/)?/, '')}`)
                                        : null;

                                      if (imgSrc) {
                                        return (
                                          <div style={{ position: 'relative', width: '64px', height: '64px', flexShrink: 0 }}>
                                            <img
                                              src={imgSrc}
                                              alt={itemName}
                                              style={{
                                                width: '64px',
                                                height: '64px',
                                                borderRadius: '8px',
                                                objectFit: 'cover',
                                                border: '1px solid #e2e8f0',
                                                background: '#ffffff',
                                                display: 'block'
                                              }}
                                              onError={(e) => {
                                                e.currentTarget.style.display = 'none';
                                                const fb = e.currentTarget.parentElement?.querySelector('.item-fallback-icon');
                                                if (fb) fb.style.display = 'flex';
                                              }}
                                            />
                                            <div
                                              className="item-fallback-icon"
                                              style={{
                                                display: 'none',
                                                width: '64px',
                                                height: '64px',
                                                borderRadius: '8px',
                                                background: '#f1f5f9',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                fontSize: '22px',
                                                color: '#94a3b8'
                                              }}
                                            >
                                              <FaBox />
                                            </div>
                                          </div>
                                        );
                                      }

                                      return (
                                        <div style={{
                                          width: '64px',
                                          height: '64px',
                                          borderRadius: '8px',
                                          background: '#f1f5f9',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          fontSize: '22px',
                                          color: '#94a3b8',
                                          flexShrink: 0
                                        }}>
                                          <FaBox />
                                        </div>
                                      );
                                    })()}
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                      <div style={{ fontSize: '15px', fontWeight: 600, color: '#1e293b', marginBottom: '4px', wordBreak: 'break-word' }}>
                                        {itemName}
                                      </div>

                                      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#64748b' }}>
                                        <span>Qty: <strong style={{ color: '#1e293b' }}>{qty}</strong></span>

                                        {variantStr && variantStr.toLowerCase() !== 'null' && variantStr.toLowerCase() !== 'undefined' && (
                                          <>
                                            <span style={{ color: '#cbd5e0' }}>|</span>
                                            <span>Variant: <strong style={{ color: '#1e293b' }}>{variantStr}</strong></span>
                                          </>
                                        )}

                                        {unitPrice > 0 && (
                                          <>
                                            <span style={{ color: '#cbd5e0' }}>|</span>
                                            <span>Rate: <strong style={{ color: '#1e293b' }}>₹{unitPrice.toFixed(2)}</strong></span>
                                          </>
                                        )}
                                      </div>
                                    </div>
                                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                                      <div style={{ fontSize: '16px', fontWeight: 700, color: '#F55D00' }}>
                                        ₹{totalPrice.toFixed(2)}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {/* Order Summary */}
                        {/* Order Summary */}
                        {(selectedOrder.summary || selectedOrder.order_summary || selectedOrder.total_amount) && (() => {
                          const orderSummary = selectedOrder.order_summary || selectedOrder.summary || {};
                          const subtotal = Number(orderSummary.subtotal || selectedOrder.total_amount || 0);
                          const deliveryCharges = Number(orderSummary.delivery_charges || 0);
                          const parcelCharges = Number(orderSummary.parcel_charges || 0);
                          const customizationCharges = Number(orderSummary.customization_charges || 0);
                          let gstTotal = Number(orderSummary.gst_total || orderSummary.tax || 0);

                          if (gstTotal === 0 && selectedOrder.items) {
                            gstTotal = selectedOrder.items.reduce((sum, item) => {
                              if (Array.isArray(item.tax_details) && item.tax_details.length > 0) {
                                return sum + item.tax_details.reduce((tSum, t) => tSum + Number(t.tax_amount || 0), 0);
                              }
                              return sum + Number(item.gst_amount || item.tax_amount || item.gst || 0);
                            }, 0);
                          }

                          const discountAmount = Number(orderSummary.discount_amount || 0);
                          const totalAmount = Number(orderSummary.total_amount || orderSummary.total || selectedOrder.final_amount || 0);
                          const paidAmount = Number(orderSummary.paid_amount || 0);
                          const remainingAmount = Number(orderSummary.remaining_amount || 0);
                          const isGroceryOrFood = (selectedOrder.business_type || selectedOrder.business_details?.business_type || '') === 'R02';

                          return (
                            <div style={{
                              background: 'white',
                              borderRadius: '16px',
                              padding: '20px',
                              boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                            }}>
                              <h3 style={{
                                margin: '0 0 16px 0',
                                fontSize: '16px',
                                fontWeight: 700,
                                color: '#1e293b'
                              }}>
                                Order Summary
                              </h3>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                {subtotal > 0 && (
                                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px' }}>
                                    <span style={{ color: '#64748b' }}>Subtotal:</span>
                                    <span style={{ fontWeight: 600, color: '#1e293b' }}>
                                      ₹{subtotal.toFixed(2)}
                                    </span>
                                  </div>
                                )}
                                {deliveryCharges > 0 && (
                                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px' }}>
                                    <span style={{ color: '#64748b' }}>Delivery Charges:</span>
                                    <span style={{ fontWeight: 600, color: '#1e293b' }}>
                                      ₹{deliveryCharges.toFixed(2)}
                                    </span>
                                  </div>
                                )}
                                {parcelCharges > 0 && (
                                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px' }}>
                                    <span style={{ color: '#64748b' }}>Parcel Charges:</span>
                                    <span style={{ fontWeight: 600, color: '#1e293b' }}>
                                      ₹{parcelCharges.toFixed(2)}
                                    </span>
                                  </div>
                                )}
                                {customizationCharges > 0 && (
                                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px' }}>
                                    <span style={{ color: '#64748b' }}>Customization Charges:</span>
                                    <span style={{ fontWeight: 600, color: '#1e293b' }}>
                                      ₹{customizationCharges.toFixed(2)}
                                    </span>
                                  </div>
                                )}
                                {gstTotal > 0 && (
                                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', cursor: 'pointer' }} onClick={() => setShowGstBreakdown(!showGstBreakdown)}>
                                    <span style={{ color: '#64748b' }}>
                                      {isGroceryOrFood ? 'GST (5%):' : 'Tax / GST:'}
                                      <span style={{ marginLeft: '4px', verticalAlign: 'middle', display: 'inline-block' }}>
                                        {showGstBreakdown ? <FaChevronUp size={12} /> : <FaChevronDown size={12} />}
                                      </span>
                                    </span>
                                    <span style={{ fontWeight: 600, color: '#1e293b' }}>
                                      ₹{gstTotal.toFixed(2)}
                                    </span>
                                  </div>
                                )}
                                {showGstBreakdown && (
                                  <div style={{ padding: '12px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '13px', color: '#64748b', marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                    {(selectedOrder.items || []).map((item, itemIdx) => {
                                      const hasDetails = Array.isArray(item.tax_details) && item.tax_details.length > 0;

                                      return (
                                        <div key={itemIdx} style={{ display: 'flex', flexDirection: 'column', gap: '4px', borderBottom: itemIdx < (selectedOrder.items || []).length - 1 ? '1px dashed #cbd5e1' : 'none', paddingBottom: itemIdx < (selectedOrder.items || []).length - 1 ? '6px' : '0' }}>
                                          {hasDetails ? (
                                            item.tax_details.map((tax, taxIdx) => {
                                              const type = tax.tax_type || 'GST';
                                              const pct = parseFloat(tax.tax_percentage || 0);
                                              const amt = Number(tax.tax_amount || 0);
                                              return (
                                                <div key={taxIdx} style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                  <span>{type} ({pct}%):</span>
                                                  <span style={{ fontWeight: 600, color: '#334155' }}>
                                                    ₹{amt.toFixed(2)}
                                                  </span>
                                                </div>
                                              );
                                            })
                                          ) : (() => {
                                            const taxAmount = Number(item.gst_amount || item.tax_amount || item.gst || 0);
                                            if (taxAmount <= 0) return null;

                                            const unitPrice = Number(item.unit_price || item.price || 0);
                                            const qty = Number(item.quantity || 1);
                                            const totalPrice = unitPrice * qty;

                                            let percentage = 5;
                                            if (item.tax_percent) percentage = Number(item.tax_percent);
                                            else if (item.gst_percentage) percentage = Number(item.gst_percentage);
                                            else if (totalPrice > 0) percentage = Math.round((taxAmount / totalPrice) * 100);

                                            const halfPercent = percentage / 2;
                                            const halfAmount = taxAmount / 2;
                                            const isInterState = selectedOrder.order_summary?.gst_jurisdiction === 'INTER_STATE';

                                            if (isInterState) {
                                              return (
                                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                  <span>IGST ({percentage}%):</span>
                                                  <span style={{ fontWeight: 600, color: '#334155' }}>₹{taxAmount.toFixed(2)}</span>
                                                </div>
                                              );
                                            }

                                            return (
                                              <React.Fragment>
                                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                  <span>CGST ({halfPercent}%):</span>
                                                  <span style={{ fontWeight: 600, color: '#334155' }}>₹{halfAmount.toFixed(2)}</span>
                                                </div>
                                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                  <span>SGST ({halfPercent}%):</span>
                                                  <span style={{ fontWeight: 600, color: '#334155' }}>₹{halfAmount.toFixed(2)}</span>
                                                </div>
                                              </React.Fragment>
                                            );
                                          })()}
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                                {discountAmount > 0 && (
                                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', color: '#10b981' }}>
                                    <span>Discount:</span>
                                    <span style={{ fontWeight: 600 }}>
                                      -₹{discountAmount.toFixed(2)}
                                    </span>
                                  </div>
                                )}
                                <div style={{ height: '1px', background: '#e2e8f0', margin: '8px 0' }}></div>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                  <span style={{ fontSize: '16px', fontWeight: 700, color: '#1e293b' }}>Total Amount:</span>
                                  <span style={{ fontSize: '20px', fontWeight: 700, color: '#F55D00' }}>
                                    ₹{totalAmount.toFixed(2)}
                                  </span>
                                </div>

                                {remainingAmount > 0 && (
                                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', color: '#ef4444' }}>
                                    <span>Remaining Amount:</span>
                                    <span style={{ fontWeight: 600 }}>
                                      ₹{remainingAmount.toFixed(2)}
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })()}
                      </div>

                    </div>

                    {/* Modal Footer Actions */}

                    <div style={{

                      padding: '20px 32px',

                      borderTop: '1px solid #e2e8f0',

                      background: 'white',

                      display: 'flex',

                      justifyContent: 'space-between',

                      alignItems: 'center',

                      borderRadius: '0 0 20px 20px'

                    }}>

                      <div style={{ display: 'flex', gap: '12px' }}>

                      </div>

                      <button

                        onClick={() => setShowDetailsModal(false)}

                        style={{

                          padding: '10px 24px',

                          background: 'linear-gradient(135deg, #F55D00 0%, #FDBF50 100%)',

                          color: 'white',

                          border: 'none',

                          borderRadius: '8px',

                          fontSize: '14px',

                          fontWeight: 600,

                          cursor: 'pointer'

                        }}

                      >

                        Close

                      </button>

                    </div>

                  </div>

                </>

              );

            } catch (renderError) {

              console.error('Modal rendering error:', renderError);

              return (

                <div style={{

                  background: 'white',

                  padding: '40px',

                  borderRadius: '16px',

                  maxWidth: '500px',

                  textAlign: 'center'

                }}>

                  <h3>Error displaying order details</h3>

                  <p>There was a problem loading the order information.</p>

                  <button

                    onClick={() => setShowDetailsModal(false)}

                    style={{

                      padding: '10px 20px',

                      background: '#667eea',

                      color: 'white',

                      border: 'none',

                      borderRadius: '8px',

                      cursor: 'pointer'

                    }}

                  >

                    Close

                  </button>

                </div>

              );

            }

          })()}

        </div>

      )}

    </div>

  );

};



export default OrderManagement;

